<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import FeatureLockedScreen from 'dashboard/components/FeatureLockedScreen.vue';
import {
  commissionLaunchesApi,
  commissionPoliciesApi,
  commissionGoalConfigsApi,
  commissionMovementTypesApi,
  commissionConsultantClosuresApi,
  commissionCompetenceClosuresApi,
  commissionManualAdjustmentsApi,
  commissionAuditLogsApi,
  commissionLaunchImportsApi,
  commissionExtraCampaignsApi,
} from 'dashboard/api/commissions';
import { productsApi } from 'dashboard/api/products';

const { t } = useI18n();
const { accountId } = useAccount();
const store = useStore();
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const currentUser = useMapGetter('getCurrentUser');
const agents = useMapGetter('agents/getAgents');

const hasFeature = computed(() =>
  isFeatureEnabled.value(accountId.value, 'commission_management')
);

// ──────────────────────────────────────────────
// TABS
// ──────────────────────────────────────────────
const activeTab = ref('dashboard');
const tabs = computed(() => [
  { id: 'dashboard', label: t('COMMISSIONS.TABS.DASHBOARD'), icon: 'i-lucide-layout-dashboard' },
  { id: 'launches', label: t('COMMISSIONS.TABS.LAUNCHES'), icon: 'i-lucide-file-plus' },
  { id: 'closure', label: t('COMMISSIONS.TABS.CLOSURE'), icon: 'i-lucide-check-square' },
  { id: 'competence', label: t('COMMISSIONS.TABS.COMPETENCE'), icon: 'i-lucide-calendar-check' },
  { id: 'policies', label: t('COMMISSIONS.TABS.POLICIES'), icon: 'i-lucide-scroll-text' },
  { id: 'import', label: t('COMMISSIONS.TABS.IMPORT'), icon: 'i-lucide-upload' },
  { id: 'audit', label: t('COMMISSIONS.TABS.AUDIT'), icon: 'i-lucide-shield-check' },
]);

// ──────────────────────────────────────────────
// SHARED: COMPETENCE FILTER
// ──────────────────────────────────────────────
const competence = ref(
  new Date().toISOString().slice(0, 7) // YYYY-MM
);

// ──────────────────────────────────────────────
// DASHBOARD
// ──────────────────────────────────────────────
const dashboardStats = ref(null);
const dashboardClosures = ref([]);
const loadingDashboard = ref(false);

const ranking = computed(() =>
  [...dashboardClosures.value].sort((a, b) => (b.val_total_final || 0) - (a.val_total_final || 0))
);

const teamMeta = computed(() => {
  const cls = dashboardClosures.value;
  if (!cls.length) return null;
  return {
    meta_previdencia: cls.reduce((s, c) => s + (parseFloat(c.meta_previdencia) || 0), 0),
    meta_risco: cls.reduce((s, c) => s + (parseFloat(c.meta_risco) || 0), 0),
    realizado_previdencia: cls.reduce((s, c) => s + (parseFloat(c.realizado_previdencia) || 0), 0),
    realizado_risco: cls.reduce((s, c) => s + (parseFloat(c.realizado_risco) || 0), 0),
    total_premiacao_plus: cls.reduce((s, c) => s + (parseFloat(c.val_premiacao_plus) || 0), 0),
    total_premiacao_volume: cls.reduce((s, c) => s + (parseFloat(c.val_premiacao_volume) || 0), 0),
    total_a_pagar: cls.reduce((s, c) => s + (parseFloat(c.val_total_final) || 0), 0),
  };
});

// Ritmo esperado: % do mês decorrido até hoje
const paceMarkerPct = computed(() => {
  const today = new Date();
  const day = today.getDate();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  return Math.round((day / daysInMonth) * 100);
});

const dashboardAlerts = computed(() => {
  const alerts = [];
  dashboardClosures.value.forEach(cl => {
    const pPct = parseFloat(cl.atingimento_previdencia_pct) || 0;
    const rPct = parseFloat(cl.atingimento_risco_pct) || 0;
    const name = cl.consultant_name || `Consultor #${cl.user_id}`;
    if (pPct < 0.8) alerts.push(`${name}: previdência em ${(pPct * 100).toFixed(1)}% da meta (abaixo de 80%)`);
    if (rPct < 0.8) alerts.push(`${name}: risco em ${(rPct * 100).toFixed(1)}% da meta (abaixo de 80%)`);
  });
  return alerts;
});

async function fetchDashboard() {
  loadingDashboard.value = true;
  try {
    const [statsRes, closuresRes] = await Promise.all([
      commissionLaunchesApi.stats({ competence: competence.value }),
      commissionConsultantClosuresApi.list({ competence: competence.value, per: 100 }),
    ]);
    dashboardStats.value = statsRes.data || null;
    dashboardClosures.value = closuresRes.data?.payload || [];
  } catch {
    dashboardStats.value = null;
    dashboardClosures.value = [];
  } finally {
    loadingDashboard.value = false;
  }
}

// ──────────────────────────────────────────────
// LAUNCHES
// ──────────────────────────────────────────────
const launches = ref([]);
const launchesTotal = ref(0);
const launchesPage = ref(1);
const launchesTotalPages = ref(1);
const loadingLaunches = ref(false);
const launchFilters = ref({ competence: competence.value, status: '', sale_class: '', user_id: '', date_from: '', date_to: '', inconsistency_only: false });
const showLaunchForm = ref(false);
const editingLaunch = ref(null);
const movementTypes = ref([]);
const products = ref([]);

const launchForm = ref({
  user_id: '',
  competence: competence.value,
  participante: '',
  cpf: '',
  proposta: '',
  inscricao: '',
  sale_date: '',
  movement_type_id: '',
  product_id: '',
  val_previdencia: '',
  val_risco_invalidez: '',
  val_risco_morte: '',
  val_aporte: '',
  val_portabilidade: '',
  notes: '',
  status: 'active',
});

async function fetchLaunches() {
  loadingLaunches.value = true;
  try {
    const params = { page: launchesPage.value };
    if (launchFilters.value.competence) params.competence = launchFilters.value.competence;
    if (launchFilters.value.status) params.status = launchFilters.value.status;
    if (launchFilters.value.sale_class) params.sale_class = launchFilters.value.sale_class;
    if (launchFilters.value.user_id) params.user_id = launchFilters.value.user_id;
    if (launchFilters.value.date_from) params.date_from = launchFilters.value.date_from;
    if (launchFilters.value.date_to) params.date_to = launchFilters.value.date_to;
    if (launchFilters.value.inconsistency_only) params.inconsistency_only = 'true';
    const res = await commissionLaunchesApi.list(params);
    launches.value = res.data?.payload || [];
    launchesTotal.value = res.data?.meta?.count || 0;
    launchesTotalPages.value = res.data?.meta?.total_pages || 1;
  } catch {
    launches.value = [];
  } finally {
    loadingLaunches.value = false;
  }
}

async function fetchMovementTypes() {
  try {
    const res = await commissionMovementTypesApi.list();
    movementTypes.value = res.data?.payload || [];
  } catch {
    movementTypes.value = [];
  }
}

async function fetchProducts() {
  try {
    const res = await productsApi.list({ active_only: true });
    products.value = res.data?.payload || [];
  } catch {
    products.value = [];
  }
}

function openNewLaunch() {
  editingLaunch.value = null;
  launchForm.value = {
    user_id: '',
    competence: launchFilters.value.competence || competence.value,
    participante: '',
    cpf: '',
    proposta: '',
    inscricao: '',
    sale_date: '',
    movement_type_id: '',
    product_id: '',
    val_previdencia: '',
    val_risco_invalidez: '',
    val_risco_morte: '',
    val_aporte: '',
    val_portabilidade: '',
    notes: '',
    status: 'active',
  };
  showLaunchForm.value = true;
}

function openEditLaunch(launch) {
  editingLaunch.value = launch;
  launchForm.value = {
    user_id: launch.user_id || '',
    competence: launch.competence,
    participante: launch.participante,
    cpf: launch.cpf,
    proposta: launch.proposta,
    inscricao: launch.inscricao,
    sale_date: launch.sale_date,
    movement_type_id: launch.movement_type_id || '',
    product_id: launch.product_id || '',
    val_previdencia: launch.val_previdencia || '',
    val_risco_invalidez: launch.val_risco_invalidez || '',
    val_risco_morte: launch.val_risco_morte || '',
    val_aporte: launch.val_aporte || '',
    val_portabilidade: launch.val_portabilidade || '',
    notes: launch.notes || '',
    status: launch.status || 'active',
  };
  showLaunchForm.value = true;
}

const savingLaunch = ref(false);
async function saveLaunch() {
  savingLaunch.value = true;
  try {
    if (editingLaunch.value) {
      await commissionLaunchesApi.update(editingLaunch.value.id, launchForm.value);
    } else {
      await commissionLaunchesApi.create(launchForm.value);
    }
    showLaunchForm.value = false;
    fetchLaunches();
    fetchDashboard();
  } catch {
    // keep form open
  } finally {
    savingLaunch.value = false;
  }
}

async function deleteLaunch(id) {
  if (!window.confirm(t('COMMISSIONS.LAUNCHES.DELETE_CONFIRM'))) return;
  try {
    await commissionLaunchesApi.delete(id);
    fetchLaunches();
    fetchDashboard();
  } catch {
    // noop
  }
}

// ──────────────────────────────────────────────
// CLOSURE (per consultant)
// ──────────────────────────────────────────────
const closures = ref([]);
const loadingClosures = ref(false);
const selectedClosure = ref(null);
const closureItems = ref([]);
const loadingClosureItems = ref(false);
const showAdjustmentForm = ref(false);
const adjustmentForm = ref({ kind: 'credit', amount: '', reason: '', notes: '' });
const savingAdjustment = ref(false);
const manualAdjustments = ref([]);

async function fetchClosures() {
  loadingClosures.value = true;
  try {
    const res = await commissionConsultantClosuresApi.byCompetence(competence.value);
    closures.value = res.data?.payload || [];
  } catch {
    closures.value = [];
  } finally {
    loadingClosures.value = false;
  }
}

async function generateClosure(id) {
  try {
    await commissionConsultantClosuresApi.generate(id);
    fetchClosures();
  } catch {
    // noop
  }
}

async function approveClosure(id) {
  try {
    await commissionConsultantClosuresApi.approve(id);
    fetchClosures();
  } catch {
    // noop
  }
}

async function markPaidClosure(id) {
  try {
    await commissionConsultantClosuresApi.markPaid(id);
    fetchClosures();
  } catch {
    // noop
  }
}

async function reopenClosure(id) {
  try {
    await commissionConsultantClosuresApi.reopen(id);
    fetchClosures();
  } catch {
    // noop
  }
}

async function selectClosure(closure) {
  selectedClosure.value = closure;
  loadingClosureItems.value = true;
  try {
    const res = await commissionConsultantClosuresApi.show(closure.id);
    const data = res.data?.payload || {};
    closureItems.value = data.items || [];
    manualAdjustments.value = data.manual_adjustments || [];
  } catch {
    closureItems.value = [];
    manualAdjustments.value = [];
  } finally {
    loadingClosureItems.value = false;
  }
}

async function saveAdjustment() {
  if (!selectedClosure.value) return;
  savingAdjustment.value = true;
  try {
    await commissionManualAdjustmentsApi.create({
      closure_id: selectedClosure.value.id,
      ...adjustmentForm.value,
    });
    showAdjustmentForm.value = false;
    adjustmentForm.value = { kind: 'credit', amount: '', reason: '', notes: '' };
    selectClosure(selectedClosure.value);
    fetchClosures();
  } catch {
    // noop
  } finally {
    savingAdjustment.value = false;
  }
}

async function deleteAdjustment(id) {
  try {
    await commissionManualAdjustmentsApi.delete(id);
    selectClosure(selectedClosure.value);
    fetchClosures();
  } catch {
    // noop
  }
}

async function createClosureForCompetence() {
  try {
    await commissionConsultantClosuresApi.create({ competence: competence.value });
    fetchClosures();
  } catch {
    // noop
  }
}

// ──────────────────────────────────────────────
// COMPETENCE (consolidated)
// ──────────────────────────────────────────────
const competenceClosures = ref([]);
const loadingCompetence = ref(false);

async function fetchCompetenceClosures() {
  loadingCompetence.value = true;
  try {
    const res = await commissionCompetenceClosuresApi.list();
    competenceClosures.value = res.data?.payload || [];
  } catch {
    competenceClosures.value = [];
  } finally {
    loadingCompetence.value = false;
  }
}

async function generateCompetenceClosure(id) {
  try {
    await commissionCompetenceClosuresApi.generate(id);
    fetchCompetenceClosures();
  } catch {
    // noop
  }
}

async function generateAllClosures() {
  try {
    await commissionCompetenceClosuresApi.create({ competence: competence.value });
    fetchClosures();
  } catch {
    // noop
  }
}

async function regenerateDashboard() {
  loadingDashboard.value = true;
  try {
    await commissionCompetenceClosuresApi.create({ competence: competence.value });
  } catch {
    // noop — pode já existir, apenas re-busca
  }
  await fetchDashboard();
}

// ──────────────────────────────────────────────
// POLICIES
// ──────────────────────────────────────────────
const policies = ref([]);
const loadingPolicies = ref(false);

async function fetchPolicies() {
  loadingPolicies.value = true;
  try {
    const res = await commissionPoliciesApi.list();
    policies.value = res.data?.payload || [];
  } catch {
    policies.value = [];
  } finally {
    loadingPolicies.value = false;
  }
}

// ──────────────────────────────────────────────
// POLICY CRUD
// ──────────────────────────────────────────────
const DEFAULT_VOLUME_TIERS = [
  { min: 0, max: 14, uniproduto: 10.0, completa: 20.0 },
  { min: 15, max: null, uniproduto: 20.0, completa: 40.0 },
];
const DEFAULT_PLUS_TIERS = [
  { min_pct: 1.0, max_pct: 1.3, bonus: 300.0 },
  { min_pct: 1.31, max_pct: 1.5, bonus: 500.0 },
  { min_pct: 1.51, max_pct: null, bonus: 750.0 },
];

const showPolicyForm = ref(false);
const editingPolicy = ref(null);
const savingPolicy = ref(false);
const policyForm = ref({
  name: '', description: '', effective_from: '', effective_until: '',
  is_active: true,
  pct_uniproduto: 25, pct_completa: 50, pct_aporte: 1,
  aporte_cap: 200000,
  volume_tiers: JSON.parse(JSON.stringify(DEFAULT_VOLUME_TIERS)),
  plus_tiers: JSON.parse(JSON.stringify(DEFAULT_PLUS_TIERS)),
});

function openNewPolicy() {
  editingPolicy.value = null;
  policyForm.value = {
    name: '', description: '', effective_from: '', effective_until: '',
    is_active: true,
    pct_uniproduto: 25, pct_completa: 50, pct_aporte: 1,
    aporte_cap: 200000,
    volume_tiers: JSON.parse(JSON.stringify(DEFAULT_VOLUME_TIERS)),
    plus_tiers: JSON.parse(JSON.stringify(DEFAULT_PLUS_TIERS)),
  };
  showPolicyForm.value = true;
}

function openEditPolicy(policy) {
  editingPolicy.value = policy;
  policyForm.value = {
    name: policy.name,
    description: policy.description || '',
    effective_from: policy.effective_from || '',
    effective_until: policy.effective_until || '',
    is_active: policy.is_active,
    pct_uniproduto: parseFloat((policy.pct_uniproduto * 100).toFixed(4)),
    pct_completa: parseFloat((policy.pct_completa * 100).toFixed(4)),
    pct_aporte: parseFloat((policy.pct_aporte * 100).toFixed(4)),
    aporte_cap: policy.aporte_cap,
    volume_tiers: JSON.parse(JSON.stringify(policy.volume_tiers || DEFAULT_VOLUME_TIERS)),
    plus_tiers: JSON.parse(JSON.stringify(policy.plus_tiers || DEFAULT_PLUS_TIERS)),
  };
  showPolicyForm.value = true;
}

function addVolumeTier() {
  policyForm.value.volume_tiers.push({ min: 0, max: null, uniproduto: 0, completa: 0 });
}
function removeVolumeTier(idx) {
  policyForm.value.volume_tiers.splice(idx, 1);
}
function addPlusTier() {
  policyForm.value.plus_tiers.push({ min_pct: 1.0, max_pct: null, bonus: 0 });
}
function removePlusTier(idx) {
  policyForm.value.plus_tiers.splice(idx, 1);
}

async function savePolicy() {
  savingPolicy.value = true;
  try {
    const payload = {
      ...policyForm.value,
      pct_uniproduto: policyForm.value.pct_uniproduto / 100,
      pct_completa: policyForm.value.pct_completa / 100,
      pct_aporte: policyForm.value.pct_aporte / 100,
      volume_tiers: policyForm.value.volume_tiers,
      plus_tiers: policyForm.value.plus_tiers,
    };
    if (editingPolicy.value) {
      await commissionPoliciesApi.update(editingPolicy.value.id, payload);
    } else {
      await commissionPoliciesApi.create(payload);
    }
    showPolicyForm.value = false;
    await fetchPolicies();
  } catch {
    // noop
  } finally {
    savingPolicy.value = false;
  }
}

async function deletePolicy(policy) {
  if (!confirm(`Excluir a política "${policy.name}"?`)) return;
  try {
    await commissionPoliciesApi.delete(policy.id);
    await fetchPolicies();
  } catch {
    // noop
  }
}

// ──────────────────────────────────────────────
// GOAL CONFIGS
// ──────────────────────────────────────────────
const goalConfigs = ref([]);
const loadingGoalConfigs = ref(false);
const showGoalForm = ref(false);
const editingGoalConfig = ref(null);
const savingGoal = ref(false);
const goalForm = ref({
  competence: '', commission_policy_id: '', user_id: '',
  goal_previdencia: 2900, goal_risco: 950, goal_total: 3850,
});

async function fetchGoalConfigs() {
  loadingGoalConfigs.value = true;
  try {
    const res = await commissionGoalConfigsApi.list();
    goalConfigs.value = res.data?.payload || [];
  } catch {
    goalConfigs.value = [];
  } finally {
    loadingGoalConfigs.value = false;
  }
}

function openNewGoalConfig() {
  editingGoalConfig.value = null;
  goalForm.value = {
    competence: competence.value,
    commission_policy_id: policies.value[0]?.id || '',
    user_id: '',
    goal_previdencia: 2900, goal_risco: 950, goal_total: 3850,
  };
  showGoalForm.value = true;
}

function openEditGoalConfig(g) {
  editingGoalConfig.value = g;
  goalForm.value = {
    competence: g.competence,
    commission_policy_id: g.commission_policy_id || '',
    user_id: g.user_id || '',
    goal_previdencia: g.goal_previdencia,
    goal_risco: g.goal_risco,
    goal_total: g.goal_total,
  };
  showGoalForm.value = true;
}

async function saveGoalConfig() {
  savingGoal.value = true;
  try {
    const payload = {
      ...goalForm.value,
      commission_policy_id: goalForm.value.commission_policy_id || null,
      user_id: goalForm.value.user_id || null,
    };
    if (editingGoalConfig.value) {
      await commissionGoalConfigsApi.update(editingGoalConfig.value.id, payload);
    } else {
      await commissionGoalConfigsApi.create(payload);
    }
    showGoalForm.value = false;
    await fetchGoalConfigs();
  } catch {
    // noop
  } finally {
    savingGoal.value = false;
  }
}

async function deleteGoalConfig(g) {
  if (!confirm(`Excluir meta da competência ${g.competence}?`)) return;
  try {
    await commissionGoalConfigsApi.delete(g.id);
    await fetchGoalConfigs();
  } catch {
    // noop
  }
}

function goalConsultantName(g) {
  if (!g.user_id) return 'Global';
  const agent = agents.value.find(a => a.id === g.user_id);
  return agent?.name || `#${g.user_id}`;
}

function goalPolicyName(g) {
  if (!g.commission_policy_id) return '—';
  const p = policies.value.find(x => x.id === g.commission_policy_id);
  return p?.name || `#${g.commission_policy_id}`;
}

// ──────────────────────────────────────────────
// EXTRA CAMPAIGNS
// ──────────────────────────────────────────────
const campaigns = ref([]);
const loadingCampaigns = ref(false);
const showCampaignForm = ref(false);
const editingCampaign = ref(null);
const savingCampaign = ref(false);
const applyingCampaign = ref(null);
const campaignForm = ref({
  name: '', description: '', competence: '',
  criteria_type: 'min_new_sales', criteria_value: 0,
  bonus_amount: 0, is_active: true,
});

const criteriaTypes = computed(() => [
  { value: 'min_new_sales',   label: 'Mín. Vendas Novas' },
  { value: 'min_previdencia', label: 'Mín. Previdência (R$)' },
  { value: 'min_risco',       label: 'Mín. Risco (R$)' },
  { value: 'min_total',       label: 'Mín. Total (R$)' },
  { value: 'manual',          label: 'Manual (todos da competência)' },
]);

async function fetchCampaigns() {
  loadingCampaigns.value = true;
  try {
    const res = await commissionExtraCampaignsApi.list();
    campaigns.value = res.data?.payload || [];
  } catch {
    campaigns.value = [];
  } finally {
    loadingCampaigns.value = false;
  }
}

function openNewCampaign() {
  editingCampaign.value = null;
  campaignForm.value = {
    name: '', description: '', competence: competence.value,
    criteria_type: 'min_new_sales', criteria_value: 0,
    bonus_amount: 0, is_active: true,
  };
  showCampaignForm.value = true;
}

function openEditCampaign(c) {
  editingCampaign.value = c;
  campaignForm.value = {
    name: c.name,
    description: c.description || '',
    competence: c.competence,
    criteria_type: c.criteria_type,
    criteria_value: c.criteria_value || 0,
    bonus_amount: c.bonus_amount,
    is_active: c.is_active,
  };
  showCampaignForm.value = true;
}

async function saveCampaign() {
  savingCampaign.value = true;
  try {
    const payload = {
      ...campaignForm.value,
      criteria_value: campaignForm.value.criteria_type === 'manual' ? null : campaignForm.value.criteria_value,
    };
    if (editingCampaign.value) {
      await commissionExtraCampaignsApi.update(editingCampaign.value.id, payload);
    } else {
      await commissionExtraCampaignsApi.create(payload);
    }
    showCampaignForm.value = false;
    await fetchCampaigns();
  } catch {
    // noop
  } finally {
    savingCampaign.value = false;
  }
}

async function deleteCampaign(c) {
  if (!confirm(`Excluir a campanha "${c.name}"?`)) return;
  try {
    await commissionExtraCampaignsApi.delete(c.id);
    await fetchCampaigns();
  } catch {
    // noop
  }
}

async function applyCampaign(c) {
  if (!confirm(`Aplicar a campanha "${c.name}" para todos os consultores elegíveis na competência ${c.competence}? Esta ação não pode ser desfeita.`)) return;
  applyingCampaign.value = c.id;
  try {
    const res = await commissionExtraCampaignsApi.apply(c.id);
    const count = res.data?.applied_count ?? 0;
    alert(`Campanha aplicada! ${count} consultor(es) bonificado(s). Regenere os fechamentos para atualizar os totais.`);
    await fetchCampaigns();
  } catch (err) {
    const msg = err?.response?.data?.error || 'Erro ao aplicar campanha.';
    alert(msg);
  } finally {
    applyingCampaign.value = null;
  }
}

function criteriaLabel(type) {
  return criteriaTypes.value.find(c => c.value === type)?.label || type;
}

// ──────────────────────────────────────────────
// AUDIT
// ──────────────────────────────────────────────
const auditLogs = ref([]);
const loadingAudit = ref(false);
const auditPage = ref(1);
const auditTotalPages = ref(1);

async function fetchAuditLogs() {
  loadingAudit.value = true;
  try {
    const res = await commissionAuditLogsApi.list({
      page: auditPage.value,
      competence: competence.value,
    });
    auditLogs.value = res.data?.payload || [];
    auditTotalPages.value = res.data?.meta?.total_pages || 1;
  } catch {
    auditLogs.value = [];
  } finally {
    loadingAudit.value = false;
  }
}

// ──────────────────────────────────────────────
// DOWNLOAD CSV (fechamento)
// ──────────────────────────────────────────────
async function downloadClosureCsv(closure) {
  try {
    const res = await commissionConsultantClosuresApi.download(closure.id);
    const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `comissao_${closure.consultant_name || closure.user_id}_${closure.competence}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch {
    // noop
  }
}

// ──────────────────────────────────────────────
// IMPORTAÇÃO
// ──────────────────────────────────────────────
const IMPORT_DB_FIELDS = [
  'user_id', 'competence', 'participante', 'cpf', 'proposta', 'inscricao',
  'sale_date', 'val_previdencia', 'val_risco_invalidez', 'val_risco_morte',
  'val_aporte', 'val_portabilidade', 'notes',
];

const importFile = ref(null);
const importFileInput = ref(null);
const importHeaders = ref([]);
const importPreviewRows = ref([]);
const importMapping = ref({}); // csvCol → dbField
const importStep = ref('upload'); // 'upload' | 'mapping' | 'done'
const importLoading = ref(false);
const importHistory = ref([]);
const loadingImportHistory = ref(false);
const importError = ref('');

async function fetchImportHistory() {
  loadingImportHistory.value = true;
  try {
    const res = await commissionLaunchImportsApi.list();
    importHistory.value = res.data?.payload || [];
  } catch {
    importHistory.value = [];
  } finally {
    loadingImportHistory.value = false;
  }
}

function onFileSelected(e) {
  importFile.value = e.target.files[0] || null;
  importStep.value = 'upload';
  importHeaders.value = [];
  importPreviewRows.value = [];
  importMapping.value = {};
  importError.value = '';
}

async function previewImport() {
  if (!importFile.value) return;
  importLoading.value = true;
  importError.value = '';
  try {
    const fd = new FormData();
    fd.append('file', importFile.value);
    const res = await commissionLaunchImportsApi.preview(fd);
    importHeaders.value = res.data?.headers || [];
    importPreviewRows.value = res.data?.rows || [];
    // Auto-mapeamento básico por nome de coluna
    importMapping.value = {};
    importHeaders.value.forEach(h => {
      const normalized = h.toLowerCase().replace(/\s+/g, '_');
      if (IMPORT_DB_FIELDS.includes(normalized)) {
        importMapping.value[h] = normalized;
      } else {
        importMapping.value[h] = '';
      }
    });
    importStep.value = 'mapping';
  } catch (e) {
    importError.value = e.response?.data?.error || t('COMMISSIONS.IMPORT.ERROR');
  } finally {
    importLoading.value = false;
  }
}

async function confirmImport() {
  if (!importFile.value) return;
  importLoading.value = true;
  importError.value = '';
  try {
    const fields = {};
    Object.entries(importMapping.value).forEach(([col, field]) => {
      if (field) fields[col] = field;
    });
    const fd = new FormData();
    fd.append('file', importFile.value);
    fd.append('mapping_config', JSON.stringify({ fields }));
    await commissionLaunchImportsApi.create(fd);
    importStep.value = 'done';
    importFile.value = null;
    fetchImportHistory();
  } catch {
    importError.value = t('COMMISSIONS.IMPORT.ERROR');
  } finally {
    importLoading.value = false;
  }
}

function resetImport() {
  importStep.value = 'upload';
  importFile.value = null;
  importHeaders.value = [];
  importPreviewRows.value = [];
  importMapping.value = {};
  importError.value = '';
  if (importFileInput.value) importFileInput.value.value = '';
}

// ──────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────
function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
}

function formatPct(val) {
  if (val == null) return '—';
  return `${(parseFloat(val) * 100).toFixed(1)}%`;
}

function closureStatusClass(status) {
  const map = {
    draft: 'bg-n-alpha-3 text-n-slate-11',
    generated: 'bg-blue-500/10 text-blue-500',
    in_review: 'bg-amber-500/10 text-amber-500',
    approved: 'bg-emerald-500/10 text-emerald-500',
    paid: 'bg-purple-500/10 text-purple-500',
  };
  return map[status] || 'bg-n-alpha-3 text-n-slate-11';
}

function launchStatusClass(status) {
  const map = {
    draft: 'bg-n-alpha-3 text-n-slate-11',
    active: 'bg-emerald-500/10 text-emerald-500',
    excluded: 'bg-red-500/10 text-red-400',
    contested: 'bg-amber-500/10 text-amber-500',
  };
  return map[status] || 'bg-n-alpha-3 text-n-slate-11';
}

function goalBarColor(pct) {
  if (pct >= 1) return '#22c55e';   // verde
  if (pct >= 0.8) return '#3b82f6'; // azul
  return '#ef4444';                  // vermelho
}

function goalBadgeStyle(pct) {
  if (pct >= 1) return 'background:#dcfce7;color:#15803d';
  if (pct >= 0.8) return 'background:#dbeafe;color:#1d4ed8';
  return 'background:#fee2e2;color:#dc2626';
}

function goalTextColor(pct) {
  if (pct >= 1) return 'color:#15803d';
  if (pct >= 0.8) return 'color:#1d4ed8';
  return 'color:#dc2626';
}

// ──────────────────────────────────────────────
// TAB INIT
// ──────────────────────────────────────────────
function goToInconsistentLaunches() {
  launchFilters.value.inconsistency_only = true;
  launchFilters.value.status = '';
  launchFilters.value.sale_class = '';
  launchFilters.value.user_id = '';
  activeTab.value = 'launches';
  fetchLaunches();
}

function onTabChange(tab) {
  activeTab.value = tab;
  if (tab === 'dashboard') fetchDashboard();
  else if (tab === 'launches') fetchLaunches();
  else if (tab === 'closure') fetchClosures();
  else if (tab === 'competence') fetchCompetenceClosures();
  else if (tab === 'policies') { fetchPolicies(); fetchGoalConfigs(); fetchCampaigns(); }
  else if (tab === 'import') { resetImport(); fetchImportHistory(); }
  else if (tab === 'audit') fetchAuditLogs();
}

watch(competence, () => {
  launchFilters.value.competence = competence.value;
  if (activeTab.value === 'dashboard') fetchDashboard();
  else if (activeTab.value === 'launches') fetchLaunches();
  else if (activeTab.value === 'closure') fetchClosures();
  else if (activeTab.value === 'audit') fetchAuditLogs();
});

watch(launchFilters, () => { launchesPage.value = 1; fetchLaunches(); }, { deep: true });

onMounted(() => {
  if (!hasFeature.value) return;
  fetchDashboard();
  fetchMovementTypes();
  fetchProducts();
  store.dispatch('agents/get');
});
</script>

<template>
  <div class="flex flex-col h-full bg-n-background overflow-hidden">
    <!-- Feature locked -->
    <FeatureLockedScreen v-if="!hasFeature" feature-key="commission_management" />

    <template v-else>
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak/30">
        <div>
          <h1 class="text-xl font-bold text-n-slate-12">{{ t('COMMISSIONS.TITLE') }}</h1>
          <p class="text-sm text-n-slate-11 mt-0.5">{{ t('COMMISSIONS.SUBTITLE') }}</p>
        </div>
        <!-- Competence picker -->
        <div class="flex items-center gap-2">
          <span class="text-xs text-n-slate-11">{{ t('COMMISSIONS.COMMON.COMPETENCE') }}:</span>
          <input
            v-model="competence"
            type="month"
            class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
      </div>

      <!-- Tabs -->
      <div class="flex gap-1 px-6 pt-4 border-b border-n-weak/30">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="[
            'flex items-center gap-1.5 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors',
            activeTab === tab.id
              ? 'bg-n-brand/10 text-n-brand border-b-2 border-n-brand'
              : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          @click="onTabChange(tab.id)"
        >
          <span :class="['text-base', tab.icon]" />
          {{ tab.label }}
        </button>
      </div>

      <!-- Tab content -->
      <div class="flex-1 overflow-y-auto p-6">

        <!-- ═══════════════════ DASHBOARD ═══════════════════ -->
        <div v-if="activeTab === 'dashboard'">
          <div v-if="loadingDashboard" class="text-center py-16 text-n-slate-11">
            <span class="i-lucide-loader-circle animate-spin text-2xl block mx-auto mb-2" />
            {{ t('COMMISSIONS.COMMON.LOADING') }}
          </div>
          <div v-else-if="!dashboardStats" class="text-center py-16 text-n-slate-11">
            {{ t('COMMISSIONS.DASHBOARD.NO_DATA') }}
          </div>
          <div v-else class="space-y-6">

            <!-- ── Barra de ação + aviso de dados do ranking ── -->
            <div class="flex items-center justify-between gap-3 flex-wrap">
              <p class="text-xs text-n-slate-11 flex items-center gap-1.5">
                <span class="i-lucide-info" />
                Os KPIs de lançamentos atualizam em tempo real. O ranking reflete o último fechamento gerado — adicione lançamentos e clique em <strong>Regenerar</strong> para atualizar.
              </p>
              <button
                class="flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg border border-n-brand text-n-brand hover:bg-n-brand/10 transition-colors shrink-0"
                :disabled="loadingDashboard"
                @click="regenerateDashboard"
              >
                <span :class="loadingDashboard ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-refresh-cw'" />
                Regenerar Fechamentos
              </button>
            </div>

            <!-- ── KPI Cards ── -->
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div class="bg-n-alpha-1 border border-n-weak/30 rounded-xl p-4 space-y-1">
                <p class="text-xs text-n-slate-11 flex items-center gap-1.5"><span class="i-lucide-file-text" />Lançamentos</p>
                <p class="text-2xl font-bold text-n-slate-12">{{ dashboardStats.total_launches ?? 0 }}</p>
                <button
                  v-if="dashboardStats.with_inconsistencies > 0"
                  class="text-xs text-red-400 hover:text-red-500 flex items-center gap-1 underline underline-offset-2 decoration-dashed"
                  @click="goToInconsistentLaunches"
                >
                  <span class="i-lucide-triangle-alert text-xs" />
                  {{ dashboardStats.with_inconsistencies }} com inconsistência
                </button>
              </div>
              <div class="bg-n-alpha-1 border border-n-weak/30 rounded-xl p-4 space-y-1">
                <p class="text-xs text-n-slate-11 flex items-center gap-1.5"><span class="i-lucide-shield" />Previdência Vendida</p>
                <p class="text-2xl font-bold text-emerald-500">{{ formatCurrency(dashboardStats.total_previdencia) }}</p>
              </div>
              <div class="bg-n-alpha-1 border border-n-weak/30 rounded-xl p-4 space-y-1">
                <p class="text-xs text-n-slate-11 flex items-center gap-1.5"><span class="i-lucide-heart-pulse" />Risco Vendido</p>
                <p class="text-2xl font-bold text-blue-500">{{ formatCurrency(dashboardStats.total_risco) }}</p>
              </div>
              <div class="bg-n-alpha-1 border border-n-weak/30 rounded-xl p-4 space-y-1">
                <p class="text-xs text-n-slate-11 flex items-center gap-1.5">
                  <span class="i-lucide-banknote" />Comissão Vendas
                  <span class="relative group ml-auto cursor-default">
                    <span class="i-lucide-info size-3.5 text-n-slate-9 hover:text-n-slate-12" />
                    <span class="pointer-events-none absolute bottom-full right-0 mb-2 w-64 rounded-lg bg-gray-900 text-white text-xs p-3 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-xl">
                      <strong class="block mb-1">Comissão de Vendas</strong>
                      Soma em tempo real das comissões calculadas por lançamento:
                      <br />· 25% sobre previdência (Uniproduto)
                      <br />· 50% sobre prev+risco (Completa)
                      <br />· 1% sobre valor aportado
                      <br /><em class="text-white/60 mt-1 block">Não inclui Premiação Volume, Plus ou Ajustes Manuais — esses entram no Total a Pagar.</em>
                    </span>
                  </span>
                </p>
                <p class="text-2xl font-bold text-n-brand">{{ formatCurrency(dashboardStats.total_commission) }}</p>
              </div>
            </div>

            <!-- ── Totais de Fechamento (se houver closures gerados) ── -->
            <template v-if="teamMeta">
              <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Premiação Volume -->
                <div class="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 space-y-1">
                  <p class="text-xs text-emerald-600 flex items-center gap-1.5">
                    <span class="i-lucide-trophy" />Premiação Volume
                    <span class="relative group ml-auto cursor-default">
                      <span class="i-lucide-info size-3.5 text-emerald-400 hover:text-emerald-600" />
                      <span class="pointer-events-none absolute bottom-full right-0 mb-2 w-64 rounded-lg bg-gray-900 text-white text-xs p-3 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-xl">
                        <strong class="block mb-1">Premiação Volume</strong>
                        Bônus por quantidade de vendas novas elegíveis no mês:
                        <br />· <strong>0–14 vendas:</strong> R$10/Uniproduto · R$20/Completa
                        <br />· <strong>15+ vendas:</strong> R$20/Uniproduto · R$40/Completa
                        <br /><em class="text-white/60 mt-1 block">A faixa se aplica a todas as vendas do mês.</em>
                      </span>
                    </span>
                  </p>
                  <p class="text-xl font-bold text-emerald-500">{{ formatCurrency(teamMeta.total_premiacao_volume) }}</p>
                </div>

                <!-- Premiação Plus -->
                <div class="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4 space-y-1">
                  <p class="text-xs text-purple-600 flex items-center gap-1.5">
                    <span class="i-lucide-star" />Premiação Plus
                    <span class="relative group ml-auto cursor-default">
                      <span class="i-lucide-info size-3.5 text-purple-400 hover:text-purple-600" />
                      <span class="pointer-events-none absolute bottom-full right-0 mb-2 w-64 rounded-lg bg-gray-900 text-white text-xs p-3 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-xl">
                        <strong class="block mb-1">Premiação Plus</strong>
                        Requer bater <strong>100% da meta de Previdência E 100% de Risco</strong> simultaneamente. Se faltar um, o bônus é R$0.
                        <br /><br />Se ambas as metas forem atingidas, o bônus é pelo % do total (prev+risco):
                        <br />· <strong>100–130%:</strong> R$300
                        <br />· <strong>131–150%:</strong> R$500
                        <br />· <strong>151%+:</strong> R$750
                      </span>
                    </span>
                  </p>
                  <p class="text-xl font-bold text-purple-500">{{ formatCurrency(teamMeta.total_premiacao_plus) }}</p>
                </div>

                <!-- Aporte/Portabilidade -->
                <div class="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 space-y-1">
                  <p class="text-xs text-amber-600 flex items-center gap-1.5">
                    <span class="i-lucide-trending-up" />Aporte/Portab.
                    <span class="relative group ml-auto cursor-default">
                      <span class="i-lucide-info size-3.5 text-amber-400 hover:text-amber-600" />
                      <span class="pointer-events-none absolute bottom-full right-0 mb-2 w-64 rounded-lg bg-gray-900 text-white text-xs p-3 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-xl">
                        <strong class="block mb-1">Volume Aporte/Portabilidade</strong>
                        Valor total aportado/portado no período. A comissão gerada é <strong>1% deste valor</strong>, com teto de <strong>R$200.000 por CPF/mês</strong>.
                        <br /><br />Não conta para as metas de Previdência ou Risco.
                      </span>
                    </span>
                  </p>
                  <p class="text-xl font-bold text-amber-500">{{ formatCurrency(dashboardStats.total_aporte) }}</p>
                </div>

                <!-- Total a Pagar -->
                <div class="bg-n-brand/5 border border-n-brand/20 rounded-xl p-4 space-y-1">
                  <p class="text-xs text-n-brand flex items-center gap-1.5">
                    <span class="i-lucide-circle-dollar-sign" />Total a Pagar
                    <span class="relative group ml-auto cursor-default">
                      <span class="i-lucide-info size-3.5 text-n-brand/60 hover:text-n-brand" />
                      <span class="pointer-events-none absolute bottom-full right-0 mb-2 w-64 rounded-lg bg-gray-900 text-white text-xs p-3 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-xl">
                        <strong class="block mb-1">Total a Pagar (equipe)</strong>
                        Soma dos fechamentos de todos os consultores:
                        <br />· Comissão de Vendas (25% uni / 50% compl.)
                        <br />· Comissão de Aporte (1%)
                        <br />· Premiação Volume
                        <br />· Premiação Plus
                        <br />· Ajustes Manuais (créditos − débitos)
                        <br /><em class="text-white/60 mt-1 block">Reflete o último fechamento gerado.</em>
                      </span>
                    </span>
                  </p>
                  <p class="text-2xl font-bold text-n-brand">{{ formatCurrency(teamMeta.total_a_pagar) }}</p>
                </div>
              </div>

              <!-- ── Barras de Meta da Equipe ── -->
              <div class="bg-n-alpha-1 border border-n-weak/30 rounded-xl p-5 space-y-5">
                <h3 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
                  <span class="i-lucide-target text-n-brand" />
                  Metas da Equipe — {{ competence }}
                </h3>

                <!-- Previdência -->
                <div class="space-y-3">
                  <div class="flex items-center justify-between gap-3">
                    <div class="flex items-center gap-2 shrink-0">
                      <span class="i-lucide-shield w-4 h-4 text-emerald-500" />
                      <span class="text-sm font-semibold text-n-slate-12">Previdência</span>
                      <span
                        class="px-2 py-0.5 rounded-full text-xs font-bold"
                        :style="goalBadgeStyle(teamMeta.meta_previdencia > 0 ? teamMeta.realizado_previdencia / teamMeta.meta_previdencia : 0)"
                      >
                        {{ teamMeta.meta_previdencia > 0 ? (teamMeta.realizado_previdencia / teamMeta.meta_previdencia * 100).toFixed(1) : '0.0' }}%
                      </span>
                    </div>
                    <div class="text-right text-xs leading-relaxed">
                      <span class="font-medium text-n-slate-12">{{ formatCurrency(teamMeta.realizado_previdencia) }}</span>
                      <span class="text-n-slate-10"> / {{ formatCurrency(teamMeta.meta_previdencia) }}</span>
                    </div>
                  </div>
                  <!-- Track with milestone marker -->
                  <div class="relative h-5 rounded-full overflow-hidden bg-slate-200">
                    <!-- 80% milestone -->
                    <div :style="{ position:'absolute', top:'0', bottom:'0', left:'80%', width:'1px', background:'#94a3b8', zIndex:10 }" />
                    <!-- Fill bar -->
                    <div
                      class="h-full rounded-full"
                      :style="{
                        width: Math.min(teamMeta.meta_previdencia > 0 ? teamMeta.realizado_previdencia / teamMeta.meta_previdencia * 100 : 0, 100) + '%',
                        background: goalBarColor(teamMeta.meta_previdencia > 0 ? teamMeta.realizado_previdencia / teamMeta.meta_previdencia : 0)
                      }"
                    />
                    <!-- Ritmo marker: orange line over fill, rendered last so it stays on top -->
                    <div :style="{ position:'absolute', top:'0', bottom:'0', left: paceMarkerPct + '%', width:'2px', background:'#f97316', zIndex:30, transform:'translateX(-50%)' }" />
                  </div>
                  <!-- Per-consultant mini bars -->
                  <div class="space-y-1.5 pt-1">
                    <div v-for="cl in dashboardClosures" :key="'prev-' + cl.id" class="flex items-center gap-2">
                      <span class="w-28 text-xs text-n-slate-10 truncate shrink-0">{{ cl.consultant_name || `Consultor #${cl.user_id}` }}</span>
                      <div class="flex-1 h-2 rounded-full overflow-hidden bg-slate-200">
                        <div
                          class="h-full rounded-full transition-all duration-500"
                          :style="{
                            width: Math.min((parseFloat(cl.atingimento_previdencia_pct) || 0) * 100, 100) + '%',
                            background: goalBarColor(parseFloat(cl.atingimento_previdencia_pct) || 0)
                          }"
                        />
                      </div>
                      <span
                        class="text-xs font-semibold w-10 text-right shrink-0"
                        :style="goalTextColor(parseFloat(cl.atingimento_previdencia_pct) || 0)"
                      >{{ ((parseFloat(cl.atingimento_previdencia_pct) || 0) * 100).toFixed(0) }}%</span>
                    </div>
                  </div>
                </div>

                <div class="border-t border-n-weak/20" />

                <!-- Risco -->
                <div class="space-y-3">
                  <div class="flex items-center justify-between gap-3">
                    <div class="flex items-center gap-2 shrink-0">
                      <span class="i-lucide-heart-pulse w-4 h-4 text-emerald-500" />
                      <span class="text-sm font-semibold text-n-slate-12">Risco</span>
                      <span
                        class="px-2 py-0.5 rounded-full text-xs font-bold"
                        :style="goalBadgeStyle(teamMeta.meta_risco > 0 ? teamMeta.realizado_risco / teamMeta.meta_risco : 0)"
                      >
                        {{ teamMeta.meta_risco > 0 ? (teamMeta.realizado_risco / teamMeta.meta_risco * 100).toFixed(1) : '0.0' }}%
                      </span>
                    </div>
                    <div class="text-right text-xs leading-relaxed">
                      <span class="font-medium text-n-slate-12">{{ formatCurrency(teamMeta.realizado_risco) }}</span>
                      <span class="text-n-slate-10"> / {{ formatCurrency(teamMeta.meta_risco) }}</span>
                    </div>
                  </div>
                  <!-- Track with milestone marker -->
                  <div class="relative h-5 rounded-full overflow-hidden bg-slate-200">
                    <!-- 80% milestone -->
                    <div :style="{ position:'absolute', top:'0', bottom:'0', left:'80%', width:'1px', background:'#94a3b8', zIndex:10 }" />
                    <!-- Fill bar -->
                    <div
                      class="h-full rounded-full"
                      :style="{
                        width: Math.min(teamMeta.meta_risco > 0 ? teamMeta.realizado_risco / teamMeta.meta_risco * 100 : 0, 100) + '%',
                        background: goalBarColor(teamMeta.meta_risco > 0 ? teamMeta.realizado_risco / teamMeta.meta_risco : 0)
                      }"
                    />
                    <!-- Ritmo marker: orange line over fill, rendered last so it stays on top -->
                    <div :style="{ position:'absolute', top:'0', bottom:'0', left: paceMarkerPct + '%', width:'2px', background:'#f97316', zIndex:30, transform:'translateX(-50%)' }" />
                  </div>
                  <!-- Per-consultant mini bars -->
                  <div class="space-y-1.5 pt-1">
                    <div v-for="cl in dashboardClosures" :key="'risco-' + cl.id" class="flex items-center gap-2">
                      <span class="w-28 text-xs text-n-slate-10 truncate shrink-0">{{ cl.consultant_name || `Consultor #${cl.user_id}` }}</span>
                      <div class="flex-1 h-2 rounded-full overflow-hidden bg-slate-200">
                        <div
                          class="h-full rounded-full transition-all duration-500"
                          :style="{
                            width: Math.min((parseFloat(cl.atingimento_risco_pct) || 0) * 100, 100) + '%',
                            background: goalBarColor(parseFloat(cl.atingimento_risco_pct) || 0)
                          }"
                        />
                      </div>
                      <span
                        class="text-xs font-semibold w-10 text-right shrink-0"
                        :style="goalTextColor(parseFloat(cl.atingimento_risco_pct) || 0)"
                      >{{ ((parseFloat(cl.atingimento_risco_pct) || 0) * 100).toFixed(0) }}%</span>
                    </div>
                  </div>
                </div>
                <!-- Legenda ritmo -->
                <div class="flex items-center gap-2 pt-1 text-[11px] text-n-slate-10">
                  <span class="inline-block w-3 shrink-0" style="height:3px; background:#f97316; border-radius:2px" />
                  <span>Linha laranja = ritmo esperado hoje ({{ paceMarkerPct }}% · dia {{ new Date().getDate() }}/{{ new Date(new Date().getFullYear(), new Date().getMonth()+1,0).getDate() }}). Barra além da linha = à frente do ritmo.</span>
                </div>
              </div>

              <!-- ── Ranking de Consultores ── -->
              <div class="bg-n-alpha-1 border border-n-weak/30 rounded-xl overflow-hidden">
                <div class="px-5 py-3 border-b border-n-weak/30 flex items-center gap-2">
                  <span class="i-lucide-list-ordered text-n-brand" />
                  <h3 class="text-sm font-semibold text-n-slate-12">Ranking de Consultores — {{ competence }}</h3>
                </div>
                <table class="w-full text-sm">
                  <thead class="bg-n-alpha-2">
                    <tr>
                      <th class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-11 w-10">#</th>
                      <th class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-11">Consultor</th>
                      <th class="text-right px-4 py-2.5 text-xs font-semibold text-n-slate-11">Prev %</th>
                      <th class="text-right px-4 py-2.5 text-xs font-semibold text-n-slate-11">Risco %</th>
                      <th class="text-right px-4 py-2.5 text-xs font-semibold text-n-slate-11">Qtd Vendas</th>
                      <th class="text-right px-4 py-2.5 text-xs font-semibold text-n-slate-11">Plus</th>
                      <th class="text-right px-4 py-2.5 text-xs font-semibold text-n-slate-11">Total</th>
                      <th class="text-left px-4 py-2.5 text-xs font-semibold text-n-slate-11">Status</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-n-weak/20">
                    <tr v-for="(cl, idx) in ranking" :key="cl.id" class="hover:bg-n-alpha-1 transition-colors">
                      <td class="px-4 py-3 text-center font-bold text-base">
                        <span v-if="idx === 0">🥇</span>
                        <span v-else-if="idx === 1">🥈</span>
                        <span v-else-if="idx === 2">🥉</span>
                        <span v-else class="text-n-slate-11 text-sm">{{ idx + 1 }}</span>
                      </td>
                      <td class="px-4 py-3">
                        <p class="font-medium text-n-slate-12">{{ cl.consultant_name || `Consultor #${cl.user_id}` }}</p>
                        <p class="text-xs text-n-slate-11">{{ cl.consultant_email }}</p>
                      </td>
                      <td class="px-4 py-3 text-right">
                        <span :class="[
                          'inline-block px-2 py-0.5 rounded-full text-xs font-bold',
                          (parseFloat(cl.atingimento_previdencia_pct) || 0) >= 1 ? 'bg-emerald-500/10 text-emerald-600' :
                          (parseFloat(cl.atingimento_previdencia_pct) || 0) >= 0.8 ? 'bg-amber-500/10 text-amber-600' :
                          'bg-red-500/10 text-red-500'
                        ]">
                          {{ formatPct(cl.atingimento_previdencia_pct) }}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-right">
                        <span :class="[
                          'inline-block px-2 py-0.5 rounded-full text-xs font-bold',
                          (parseFloat(cl.atingimento_risco_pct) || 0) >= 1 ? 'bg-emerald-500/10 text-emerald-600' :
                          (parseFloat(cl.atingimento_risco_pct) || 0) >= 0.8 ? 'bg-amber-500/10 text-amber-600' :
                          'bg-red-500/10 text-red-500'
                        ]">
                          {{ formatPct(cl.atingimento_risco_pct) }}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-right text-n-slate-12 font-medium">
                        {{ (parseInt(cl.qtd_completas) || 0) + (parseInt(cl.qtd_uniproduto) || 0) }}
                        <span class="text-xs text-n-slate-11 block">{{ cl.qtd_completas }} compl. · {{ cl.qtd_uniproduto }} uni</span>
                      </td>
                      <td class="px-4 py-3 text-right font-medium text-purple-500">{{ formatCurrency(cl.val_premiacao_plus) }}</td>
                      <td class="px-4 py-3 text-right font-bold text-n-brand">{{ formatCurrency(cl.val_total_final) }}</td>
                      <td class="px-4 py-3">
                        <span :class="[
                          'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                          closureStatusClass(cl.status)
                        ]">
                          {{ t(`COMMISSIONS.CLOSURE.STATUS.${cl.status}`) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>

            <!-- ── Sem fechamentos: prompt para gerar ── -->
            <div v-else class="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5 flex items-start gap-3">
              <span class="i-lucide-info text-blue-400 text-xl shrink-0 mt-0.5" />
              <div>
                <p class="text-sm font-medium text-n-slate-12">Ranking indisponível</p>
                <p class="text-xs text-n-slate-11 mt-1">Os fechamentos desta competência ainda não foram gerados. Vá à aba <strong>Fechamento por Consultor</strong> e clique em <strong>Gerar Fechamentos</strong> para calcular metas, atingimentos e premiações.</p>
              </div>
            </div>

            <!-- ── Alertas ── -->
            <div v-if="dashboardAlerts.length > 0" class="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
              <p class="text-sm font-semibold text-amber-500 mb-3 flex items-center gap-2">
                <span class="i-lucide-alert-triangle" /> Atenção — Consultores abaixo de 80% da meta
              </p>
              <ul class="space-y-1">
                <li v-for="(alert, i) in dashboardAlerts" :key="i" class="flex items-start gap-2 text-sm text-n-slate-11">
                  <span class="i-lucide-chevron-right text-amber-400 mt-0.5 shrink-0" />
                  {{ alert }}
                </li>
              </ul>
            </div>
            <div v-else-if="teamMeta" class="text-sm text-emerald-500 flex items-center gap-2">
              <span class="i-lucide-check-circle" />
              Todos os consultores estão acima de 80% das metas. Ótimo desempenho!
            </div>

          </div>
        </div>

        <!-- ═══════════════════ LAUNCHES ═══════════════════ -->
        <div v-else-if="activeTab === 'launches'" class="space-y-4">
          <!-- Filters row -->
          <div class="flex items-center gap-2 flex-wrap">
            <!-- Consultor -->
            <select
              v-model="launchFilters.user_id"
              class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-alpha-1 text-n-slate-12"
            >
              <option value="">Consultor: Todos</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
            </select>
            <!-- Status -->
            <select
              v-model="launchFilters.status"
              class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-alpha-1 text-n-slate-12"
            >
              <option value="">Status: Todos</option>
              <option value="active">Ativo</option>
              <option value="draft">Rascunho</option>
              <option value="excluded">Excluído</option>
              <option value="contested">Contestado</option>
            </select>
            <!-- Classe de venda -->
            <select
              v-model="launchFilters.sale_class"
              class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-alpha-1 text-n-slate-12"
            >
              <option value="">Tipo: Todos</option>
              <option value="nova_uniproduto">Venda Nova Uniproduto</option>
              <option value="nova_completa">Venda Nova Completa</option>
              <option value="alteracao">Alteração</option>
              <option value="suplementar">Suplementar</option>
              <option value="aporte_portabilidade">Aporte/Portabilidade</option>
            </select>
            <!-- Data de -->
            <div class="flex items-center gap-1.5 text-xs text-n-slate-11">
              <span>De</span>
              <input
                v-model="launchFilters.date_from"
                type="date"
                class="text-sm border border-n-weak rounded-lg px-2 py-1.5 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
              <span>até</span>
              <input
                v-model="launchFilters.date_to"
                type="date"
                class="text-sm border border-n-weak rounded-lg px-2 py-1.5 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </div>
            <!-- Só com inconsistências -->
            <button
              class="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors"
              :class="launchFilters.inconsistency_only ? 'border-red-400 bg-red-400/10 text-red-500 font-medium' : 'border-n-weak text-n-slate-11 hover:border-red-300 hover:text-red-400'"
              @click="launchFilters.inconsistency_only = !launchFilters.inconsistency_only"
            >
              <span class="i-lucide-triangle-alert text-xs" />
              Inconsistências
            </button>
            <!-- Limpar filtros -->
            <button
              v-if="launchFilters.user_id || launchFilters.status || launchFilters.sale_class || launchFilters.date_from || launchFilters.date_to || launchFilters.inconsistency_only"
              class="text-xs text-n-slate-11 hover:text-red-400 flex items-center gap-1"
              @click="launchFilters = { competence: launchFilters.competence, status: '', sale_class: '', user_id: '', date_from: '', date_to: '', inconsistency_only: false }"
            >
              <span class="i-lucide-x" /> Limpar filtros
            </button>
            <div class="ml-auto">
              <button
                class="flex items-center gap-1.5 bg-n-brand text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-n-brand/90 transition-colors"
                @click="openNewLaunch"
              >
                <span class="i-lucide-plus" />
                {{ t('COMMISSIONS.LAUNCHES.NEW') }}
              </button>
            </div>
          </div>

          <!-- Table -->
          <div v-if="loadingLaunches" class="text-center py-12 text-n-slate-11">{{ t('COMMISSIONS.COMMON.LOADING') }}</div>
          <div v-else-if="launches.length === 0" class="text-center py-12 text-n-slate-11">{{ t('COMMISSIONS.LAUNCHES.NO_DATA') }}</div>
          <div v-else class="border border-n-weak/30 rounded-xl overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-n-alpha-2 border-b border-n-weak/30">
                <tr>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">Consultor</th>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.LAUNCHES.TABLE.PARTICIPANTE') }}</th>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.LAUNCHES.TABLE.CPF') }}</th>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.LAUNCHES.TABLE.SALE_CLASS') }}</th>
                  <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.LAUNCHES.TABLE.VAL_TOTAL') }}</th>
                  <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.LAUNCHES.TABLE.COMMISSION') }}</th>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.LAUNCHES.TABLE.STATUS') }}</th>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.LAUNCHES.TABLE.DATE') }}</th>
                  <th class="px-4 py-3" />
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak/20">
                <template v-for="launch in launches" :key="launch.id">
                  <tr
                    class="hover:bg-n-alpha-1 transition-colors"
                    :class="launch.inconsistency_flags?.length ? 'bg-red-400/5' : ''"
                  >
                    <td class="px-4 py-3">
                      <p class="font-medium text-n-slate-12 text-xs">{{ launch.user?.name || '—' }}</p>
                    </td>
                    <td class="px-4 py-3 font-medium text-n-slate-12">{{ launch.participante || '—' }}</td>
                    <td class="px-4 py-3 text-n-slate-11 font-mono text-xs">{{ launch.cpf || '—' }}</td>
                    <td class="px-4 py-3 text-n-slate-11 text-xs">{{ t(`COMMISSIONS.LAUNCHES.SALE_CLASS.${launch.sale_class}`) }}</td>
                    <td class="px-4 py-3 text-right font-medium text-n-slate-12">{{ formatCurrency(launch.val_total) }}</td>
                    <td class="px-4 py-3 text-right font-medium text-emerald-500">{{ formatCurrency(launch.commission_amount) }}</td>
                    <td class="px-4 py-3">
                      <span :class="['inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', launchStatusClass(launch.status)]">
                        {{ t(`COMMISSIONS.LAUNCHES.STATUS.${launch.status}`) }}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-n-slate-11 text-xs">{{ launch.sale_date || '—' }}</td>
                    <td class="px-4 py-3">
                      <div class="flex items-center gap-2">
                        <span
                          v-if="launch.inconsistency_flags?.length"
                          class="i-lucide-triangle-alert text-red-400 text-base shrink-0"
                          :title="launch.inconsistency_flags.join(' | ')"
                        />
                        <button class="text-n-slate-11 hover:text-n-brand" @click="openEditLaunch(launch)">
                          <span class="i-lucide-pencil text-base" />
                        </button>
                        <button class="text-n-slate-11 hover:text-red-400" @click="deleteLaunch(launch.id)">
                          <span class="i-lucide-trash-2 text-base" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  <!-- Inconsistency flags sub-row -->
                  <tr v-if="launch.inconsistency_flags?.length" :key="'flags-' + launch.id" class="bg-red-400/5">
                    <td colspan="9" class="px-4 pb-2 pt-0">
                      <div class="flex items-start gap-1.5 flex-wrap">
                        <span class="text-[10px] text-red-400 font-semibold uppercase tracking-wide shrink-0 mt-0.5">Inconsistências:</span>
                        <span
                          v-for="flag in launch.inconsistency_flags"
                          :key="flag"
                          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-400/15 text-red-500 text-[10px] font-medium"
                        >
                          {{ flag }}
                        </span>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="launchesTotalPages > 1" class="flex justify-center gap-2">
            <button
              :disabled="launchesPage <= 1"
              class="px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-11 disabled:opacity-40"
              @click="launchesPage--; fetchLaunches()"
            >←</button>
            <span class="px-3 py-1.5 text-sm text-n-slate-11">{{ launchesPage }} / {{ launchesTotalPages }}</span>
            <button
              :disabled="launchesPage >= launchesTotalPages"
              class="px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-11 disabled:opacity-40"
              @click="launchesPage++; fetchLaunches()"
            >→</button>
          </div>
        </div>

        <!-- ═══════════════════ CLOSURE ═══════════════════ -->
        <div v-else-if="activeTab === 'closure'" class="space-y-4">
          <div class="flex items-center gap-3">
            <button
              class="flex items-center gap-1.5 bg-n-brand text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-n-brand/90 transition-colors"
              @click="createClosureForCompetence"
            >
              <span class="i-lucide-calculator" />
              {{ t('COMMISSIONS.CLOSURE.GENERATE') }}
            </button>
          </div>

          <div v-if="loadingClosures" class="text-center py-12 text-n-slate-11">{{ t('COMMISSIONS.COMMON.LOADING') }}</div>
          <div v-else-if="closures.length === 0" class="text-center py-12 text-n-slate-11">{{ t('COMMISSIONS.CLOSURE.NO_DATA') }}</div>
          <div v-else class="grid gap-4 lg:grid-cols-2">
            <!-- Left: closures list -->
            <div class="border border-n-weak/30 rounded-xl overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-n-alpha-2 border-b border-n-weak/30">
                  <tr>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.CLOSURE.TABLE.CONSULTANT') }}</th>
                    <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.CLOSURE.TABLE.TOTAL') }}</th>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.CLOSURE.TABLE.STATUS') }}</th>
                    <th class="px-4 py-3" />
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak/20">
                  <tr
                    v-for="cl in closures"
                    :key="cl.id"
                    :class="['hover:bg-n-alpha-1 transition-colors cursor-pointer', selectedClosure?.id === cl.id ? 'bg-n-brand/5' : '']"
                    @click="selectClosure(cl)"
                  >
                    <td class="px-4 py-3 font-medium text-n-slate-12">{{ cl.consultant_name || cl.user_id }}</td>
                    <td class="px-4 py-3 text-right font-medium text-n-brand">{{ formatCurrency(cl.val_total_final) }}</td>
                    <td class="px-4 py-3">
                      <span :class="['inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', closureStatusClass(cl.status)]">
                        {{ t(`COMMISSIONS.CLOSURE.STATUS.${cl.status}`) }}
                      </span>
                    </td>
                    <td class="px-4 py-3">
                      <div class="flex items-center gap-1">
                        <button v-if="['draft','generated'].includes(cl.status)" class="text-xs text-n-brand hover:underline" @click.stop="generateClosure(cl.id)">{{ t('COMMISSIONS.CLOSURE.REGENERATE') }}</button>
                        <button v-if="cl.status === 'generated'" class="text-xs text-emerald-500 hover:underline ml-1" @click.stop="approveClosure(cl.id)">{{ t('COMMISSIONS.CLOSURE.APPROVE') }}</button>
                        <button v-if="cl.status === 'approved'" class="text-xs text-purple-500 hover:underline ml-1" @click.stop="markPaidClosure(cl.id)">{{ t('COMMISSIONS.CLOSURE.MARK_PAID') }}</button>
                        <button v-if="['approved','paid'].includes(cl.status)" class="text-xs text-amber-500 hover:underline ml-1" @click.stop="reopenClosure(cl.id)">{{ t('COMMISSIONS.CLOSURE.REOPEN') }}</button>
                        <button class="text-xs text-n-slate-11 hover:text-n-brand ml-1" title="Baixar CSV" @click.stop="downloadClosureCsv(cl)">
                          <span class="i-lucide-download text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Right: closure detail -->
            <div v-if="selectedClosure" class="border border-n-weak/30 rounded-xl p-4 space-y-4">
              <div class="flex items-center justify-between">
                <h3 class="font-semibold text-n-slate-12">{{ selectedClosure.consultant_name || 'Consultor' }}</h3>
                <button
                  class="flex items-center gap-1 text-xs text-n-brand border border-n-brand/30 rounded-lg px-3 py-1.5 hover:bg-n-brand/10"
                  @click="showAdjustmentForm = !showAdjustmentForm"
                >
                  <span class="i-lucide-plus-circle" />
                  {{ t('COMMISSIONS.CLOSURE.DETAIL.ADD_ADJUSTMENT') }}
                </button>
              </div>

              <!-- Summary grid -->
              <div class="grid grid-cols-2 gap-2 text-xs">
                <div class="bg-n-alpha-1 rounded-lg p-2">
                  <p class="text-n-slate-11">{{ t('COMMISSIONS.CLOSURE.TABLE.COMISSAO_VENDAS') }}</p>
                  <p class="font-semibold text-n-slate-12 mt-0.5">{{ formatCurrency(selectedClosure.val_comissao_vendas) }}</p>
                </div>
                <div class="bg-n-alpha-1 rounded-lg p-2">
                  <p class="text-n-slate-11">{{ t('COMMISSIONS.CLOSURE.TABLE.COMISSAO_APORTE') }}</p>
                  <p class="font-semibold text-n-slate-12 mt-0.5">{{ formatCurrency(selectedClosure.val_comissao_aporte) }}</p>
                </div>
                <div class="bg-n-alpha-1 rounded-lg p-2">
                  <p class="text-n-slate-11">{{ t('COMMISSIONS.CLOSURE.TABLE.PREMIACAO_VOLUME') }}</p>
                  <p class="font-semibold text-n-slate-12 mt-0.5">{{ formatCurrency(selectedClosure.val_premiacao_volume) }}</p>
                </div>
                <div class="bg-n-alpha-1 rounded-lg p-2">
                  <p class="text-n-slate-11">{{ t('COMMISSIONS.CLOSURE.TABLE.PREMIACAO_PLUS') }}</p>
                  <p class="font-semibold text-n-slate-12 mt-0.5">{{ formatCurrency(selectedClosure.val_premiacao_plus) }}</p>
                </div>
                <div class="bg-n-alpha-1 rounded-lg p-2">
                  <p class="text-n-slate-11">{{ t('COMMISSIONS.CLOSURE.TABLE.AJUSTES') }}</p>
                  <p class="font-semibold text-n-slate-12 mt-0.5">{{ formatCurrency(selectedClosure.val_ajustes) }}</p>
                </div>
                <div class="bg-n-brand/10 rounded-lg p-2">
                  <p class="text-n-brand">{{ t('COMMISSIONS.CLOSURE.TABLE.TOTAL') }}</p>
                  <p class="font-bold text-n-brand mt-0.5">{{ formatCurrency(selectedClosure.val_total_final) }}</p>
                </div>
              </div>

              <!-- Manual adjustments form -->
              <div v-if="showAdjustmentForm" class="bg-n-alpha-1 rounded-lg p-3 space-y-3 border border-n-weak/30">
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.CLOSURE.ADJUSTMENT.KIND') }}</label>
                    <select v-model="adjustmentForm.kind" class="w-full text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12">
                      <option value="credit">{{ t('COMMISSIONS.CLOSURE.ADJUSTMENT.CREDIT') }}</option>
                      <option value="debit">{{ t('COMMISSIONS.CLOSURE.ADJUSTMENT.DEBIT') }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.CLOSURE.ADJUSTMENT.AMOUNT') }}</label>
                    <input v-model="adjustmentForm.amount" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12" />
                  </div>
                </div>
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.CLOSURE.ADJUSTMENT.REASON') }}</label>
                  <input v-model="adjustmentForm.reason" type="text" class="w-full text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12" />
                </div>
                <div class="flex gap-2">
                  <button
                    :disabled="savingAdjustment"
                    class="flex-1 bg-n-brand text-white text-sm font-medium py-1.5 rounded-lg hover:bg-n-brand/90 disabled:opacity-50"
                    @click="saveAdjustment"
                  >{{ t('COMMISSIONS.CLOSURE.ADJUSTMENT.SAVE') }}</button>
                  <button class="text-sm text-n-slate-11 px-3" @click="showAdjustmentForm = false">{{ t('COMMISSIONS.CLOSURE.ADJUSTMENT.CANCEL') }}</button>
                </div>
              </div>

              <!-- Manual adjustments list -->
              <div v-if="manualAdjustments.length > 0" class="space-y-1">
                <p class="text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.CLOSURE.DETAIL.MANUAL_ADJUSTMENTS') }}</p>
                <div v-for="adj in manualAdjustments" :key="adj.id" class="flex items-center justify-between text-sm bg-n-alpha-1 rounded-lg px-3 py-2">
                  <div>
                    <span :class="['font-medium', adj.kind === 'credit' ? 'text-emerald-500' : 'text-red-400']">
                      {{ adj.kind === 'credit' ? '+' : '-' }}{{ formatCurrency(adj.amount) }}
                    </span>
                    <span class="text-n-slate-11 ml-2 text-xs">{{ adj.reason }}</span>
                  </div>
                  <button class="text-n-slate-11 hover:text-red-400" @click="deleteAdjustment(adj.id)">
                    <span class="i-lucide-x text-sm" />
                  </button>
                </div>
              </div>

              <!-- Items snapshot -->
              <div v-if="loadingClosureItems" class="text-center text-sm text-n-slate-11">{{ t('COMMISSIONS.COMMON.LOADING') }}</div>
              <div v-else-if="closureItems.length > 0" class="space-y-1">
                <p class="text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.CLOSURE.DETAIL.SNAPSHOT') }} ({{ closureItems.length }})</p>
                <div class="max-h-48 overflow-y-auto border border-n-weak/20 rounded-lg">
                  <table class="w-full text-xs">
                    <thead class="sticky top-0 bg-n-alpha-2">
                      <tr>
                        <th class="text-left px-3 py-2 text-n-slate-11">Participante</th>
                        <th class="text-left px-3 py-2 text-n-slate-11">Classe</th>
                        <th class="text-right px-3 py-2 text-n-slate-11">Base</th>
                        <th class="text-right px-3 py-2 text-n-slate-11">Comissão</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-n-weak/10">
                      <tr v-for="item in closureItems" :key="item.id" class="hover:bg-n-alpha-1">
                        <td class="px-3 py-1.5 text-n-slate-12">{{ item.participante || '—' }}</td>
                        <td class="px-3 py-1.5 text-n-slate-11">{{ item.sale_class }}</td>
                        <td class="px-3 py-1.5 text-right text-n-slate-12">{{ formatCurrency(item.val_base) }}</td>
                        <td class="px-3 py-1.5 text-right text-emerald-500 font-medium">{{ formatCurrency(item.val_comissao) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div v-else class="border border-n-weak/30 rounded-xl p-8 flex items-center justify-center text-sm text-n-slate-11">
              Selecione um consultor para ver os detalhes
            </div>
          </div>
        </div>

        <!-- ═══════════════════ COMPETENCE ═══════════════════ -->
        <div v-else-if="activeTab === 'competence'" class="space-y-4">
          <div class="flex items-center gap-3">
            <button
              class="flex items-center gap-1.5 bg-n-brand text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-n-brand/90 transition-colors"
              @click="generateAllClosures"
            >
              <span class="i-lucide-refresh-cw" />
              {{ t('COMMISSIONS.COMPETENCE.GENERATE_ALL') }}
            </button>
          </div>

          <div v-if="loadingCompetence" class="text-center py-12 text-n-slate-11">{{ t('COMMISSIONS.COMMON.LOADING') }}</div>
          <div v-else-if="competenceClosures.length === 0" class="text-center py-12 text-n-slate-11">{{ t('COMMISSIONS.COMPETENCE.NO_DATA') }}</div>
          <div v-else class="border border-n-weak/30 rounded-xl overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-n-alpha-2 border-b border-n-weak/30">
                <tr>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.COMPETENCE.TABLE.COMPETENCE') }}</th>
                  <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.COMPETENCE.TABLE.CONSULTANTS') }}</th>
                  <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.COMPETENCE.TABLE.COMISSAO_VENDAS') }}</th>
                  <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.COMPETENCE.TABLE.PREMIACAO_VOLUME') }}</th>
                  <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.COMPETENCE.TABLE.PREMIACAO_PLUS') }}</th>
                  <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.COMPETENCE.TABLE.TOTAL') }}</th>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.COMPETENCE.TABLE.STATUS') }}</th>
                  <th class="px-4 py-3" />
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak/20">
                <tr v-for="cc in competenceClosures" :key="cc.id" class="hover:bg-n-alpha-1">
                  <td class="px-4 py-3 font-mono font-medium text-n-slate-12">{{ cc.competence }}</td>
                  <td class="px-4 py-3 text-right text-n-slate-11">{{ cc.total_consultants }}</td>
                  <td class="px-4 py-3 text-right text-n-slate-12">{{ formatCurrency(cc.total_val_comissao_vendas) }}</td>
                  <td class="px-4 py-3 text-right text-n-slate-12">{{ formatCurrency(cc.total_val_premiacao_volume) }}</td>
                  <td class="px-4 py-3 text-right text-n-slate-12">{{ formatCurrency(cc.total_val_premiacao_plus) }}</td>
                  <td class="px-4 py-3 text-right font-bold text-n-brand">{{ formatCurrency(cc.total_a_pagar) }}</td>
                  <td class="px-4 py-3">
                    <span :class="['inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', closureStatusClass(cc.status)]">
                      {{ t(`COMMISSIONS.CLOSURE.STATUS.${cc.status}`) }}
                    </span>
                  </td>
                  <td class="px-4 py-3">
                    <button class="text-xs text-n-brand hover:underline" @click="generateCompetenceClosure(cc.id)">
                      {{ t('COMMISSIONS.CLOSURE.REGENERATE') }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ═══════════════════ POLICIES ═══════════════════ -->
        <div v-else-if="activeTab === 'policies'" class="space-y-8">

          <!-- POLÍTICAS DE COMISSÃO -->
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-base font-semibold text-n-slate-12">Políticas de Comissão</h2>
                <p class="text-xs text-n-slate-11 mt-0.5">Percentuais, teto de aporte e faixas de premiação.</p>
              </div>
              <button class="flex items-center gap-1.5 text-sm font-medium text-white bg-n-brand px-3 py-1.5 rounded-lg hover:bg-n-brand/90 transition-colors" @click="openNewPolicy">
                <span class="i-lucide-plus text-sm" />Nova Política
              </button>
            </div>
            <div v-if="loadingPolicies" class="text-center py-8 text-n-slate-11 text-sm">{{ t('COMMISSIONS.COMMON.LOADING') }}</div>
            <div v-else-if="policies.length === 0" class="text-center py-8 text-n-slate-11 text-sm">Nenhuma política cadastrada.</div>
            <div v-else class="border border-n-weak/30 rounded-xl overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-n-alpha-2 border-b border-n-weak/30">
                  <tr>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">Nome</th>
                    <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">Uniproduto</th>
                    <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">Completa</th>
                    <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">Aporte %</th>
                    <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">Teto Aporte</th>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">Vigência</th>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">Status</th>
                    <th class="px-4 py-3" />
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak/20">
                  <tr v-for="policy in policies" :key="policy.id" class="hover:bg-n-alpha-1">
                    <td class="px-4 py-3 font-medium text-n-slate-12">{{ policy.name }}</td>
                    <td class="px-4 py-3 text-right text-n-slate-11">{{ formatPct(policy.pct_uniproduto) }}</td>
                    <td class="px-4 py-3 text-right text-n-slate-11">{{ formatPct(policy.pct_completa) }}</td>
                    <td class="px-4 py-3 text-right text-n-slate-11">{{ formatPct(policy.pct_aporte) }}</td>
                    <td class="px-4 py-3 text-right text-n-slate-11">{{ formatCurrency(policy.aporte_cap) }}</td>
                    <td class="px-4 py-3 text-n-slate-11 text-xs">
                      {{ policy.effective_from || '∞' }}{{ policy.effective_until ? ` → ${policy.effective_until}` : '' }}
                    </td>
                    <td class="px-4 py-3">
                      <span :class="['text-xs font-medium', policy.is_active ? 'text-emerald-500' : 'text-n-slate-11']">
                        {{ policy.is_active ? '✓ Ativa' : 'Inativa' }}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-right">
                      <button class="text-xs text-n-brand hover:underline mr-3" @click="openEditPolicy(policy)">Editar</button>
                      <button class="text-xs text-red-500 hover:underline" @click="deletePolicy(policy)">Excluir</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- METAS DE EQUIPE -->
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-base font-semibold text-n-slate-12">Metas de Equipe</h2>
                <p class="text-xs text-n-slate-11 mt-0.5">Metas por competência usadas para cálculo de premiação Plus.</p>
              </div>
              <button class="flex items-center gap-1.5 text-sm font-medium text-white bg-n-brand px-3 py-1.5 rounded-lg hover:bg-n-brand/90 transition-colors" @click="openNewGoalConfig">
                <span class="i-lucide-plus text-sm" />Nova Meta
              </button>
            </div>
            <div v-if="loadingGoalConfigs" class="text-center py-8 text-n-slate-11 text-sm">{{ t('COMMISSIONS.COMMON.LOADING') }}</div>
            <div v-else-if="goalConfigs.length === 0" class="text-center py-8 text-n-slate-11 text-sm">Nenhuma meta cadastrada.</div>
            <div v-else class="border border-n-weak/30 rounded-xl overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-n-alpha-2 border-b border-n-weak/30">
                  <tr>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">Competência</th>
                    <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">Meta Prev.</th>
                    <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">Meta Risco</th>
                    <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">Meta Total</th>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">Política</th>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">Consultor</th>
                    <th class="px-4 py-3" />
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak/20">
                  <tr v-for="g in goalConfigs" :key="g.id" class="hover:bg-n-alpha-1">
                    <td class="px-4 py-3 font-medium text-n-slate-12">{{ g.competence }}</td>
                    <td class="px-4 py-3 text-right text-n-slate-11">{{ formatCurrency(g.goal_previdencia) }}</td>
                    <td class="px-4 py-3 text-right text-n-slate-11">{{ formatCurrency(g.goal_risco) }}</td>
                    <td class="px-4 py-3 text-right text-n-slate-11">{{ formatCurrency(g.goal_total) }}</td>
                    <td class="px-4 py-3 text-n-slate-11 text-xs">{{ goalPolicyName(g) }}</td>
                    <td class="px-4 py-3 text-xs">
                      <span :class="g.user_id ? 'text-n-slate-12' : 'text-n-slate-11 italic'">{{ goalConsultantName(g) }}</span>
                    </td>
                    <td class="px-4 py-3 text-right">
                      <button class="text-xs text-n-brand hover:underline mr-3" @click="openEditGoalConfig(g)">Editar</button>
                      <button class="text-xs text-red-500 hover:underline" @click="deleteGoalConfig(g)">Excluir</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- CAMPANHAS EXTRAS -->
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-base font-semibold text-n-slate-12">Campanhas Extras</h2>
                <p class="text-xs text-n-slate-11 mt-0.5">Bônus pontuais por competência com critério configurável. Ao aplicar, cria ajustes automáticos nos fechamentos.</p>
              </div>
              <button class="flex items-center gap-1.5 text-sm font-medium text-white bg-n-brand px-3 py-1.5 rounded-lg hover:bg-n-brand/90 transition-colors" @click="openNewCampaign">
                <span class="i-lucide-plus text-sm" />Nova Campanha
              </button>
            </div>
            <div v-if="loadingCampaigns" class="text-center py-8 text-n-slate-11 text-sm">{{ t('COMMISSIONS.COMMON.LOADING') }}</div>
            <div v-else-if="campaigns.length === 0" class="text-center py-8 text-n-slate-11 text-sm">Nenhuma campanha cadastrada.</div>
            <div v-else class="border border-n-weak/30 rounded-xl overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-n-alpha-2 border-b border-n-weak/30">
                  <tr>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">Nome</th>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">Competência</th>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">Critério</th>
                    <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">Bônus/consultor</th>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">Status</th>
                    <th class="px-4 py-3" />
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak/20">
                  <tr v-for="c in campaigns" :key="c.id" class="hover:bg-n-alpha-1">
                    <td class="px-4 py-3 font-medium text-n-slate-12">{{ c.name }}</td>
                    <td class="px-4 py-3 text-n-slate-11">{{ c.competence }}</td>
                    <td class="px-4 py-3 text-n-slate-11 text-xs">
                      {{ criteriaLabel(c.criteria_type) }}
                      <span v-if="c.criteria_type !== 'manual' && c.criteria_value"> ≥ {{ c.criteria_value }}</span>
                    </td>
                    <td class="px-4 py-3 text-right font-medium text-emerald-600">{{ formatCurrency(c.bonus_amount) }}</td>
                    <td class="px-4 py-3">
                      <span v-if="c.applied_at" class="text-xs font-medium text-emerald-500">
                        ✓ Aplicada em {{ new Date(c.applied_at).toLocaleDateString('pt-BR') }}
                      </span>
                      <span v-else class="text-xs font-medium text-amber-500">Pendente</span>
                    </td>
                    <td class="px-4 py-3 text-right flex items-center justify-end gap-3">
                      <button
                        v-if="!c.applied_at"
                        :disabled="applyingCampaign === c.id"
                        class="text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1 rounded-md disabled:opacity-50 transition-colors"
                        @click="applyCampaign(c)"
                      >{{ applyingCampaign === c.id ? 'Aplicando...' : 'Aplicar' }}</button>
                      <button v-if="!c.applied_at" class="text-xs text-n-brand hover:underline" @click="openEditCampaign(c)">Editar</button>
                      <button class="text-xs text-red-500 hover:underline" @click="deleteCampaign(c)">Excluir</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>

        <!-- ═══════════════════ IMPORTAÇÃO ═══════════════════ -->
        <div v-else-if="activeTab === 'import'" class="space-y-6 max-w-3xl">
          <div>
            <h2 class="text-base font-semibold text-n-slate-12">{{ t('COMMISSIONS.IMPORT.TITLE') }}</h2>
            <p class="text-sm text-n-slate-11 mt-0.5">{{ t('COMMISSIONS.IMPORT.SUBTITLE') }}</p>
          </div>

          <!-- Step: Upload -->
          <div v-if="importStep === 'upload'" class="space-y-4">
            <div class="border-2 border-dashed border-n-weak/50 rounded-xl p-8 text-center hover:border-n-brand/40 transition-colors">
              <span class="i-lucide-upload-cloud text-4xl text-n-slate-11 block mb-3" />
              <p class="text-sm font-medium text-n-slate-12 mb-1">{{ t('COMMISSIONS.IMPORT.UPLOAD_LABEL') }}</p>
              <p class="text-xs text-n-slate-11 mb-4">{{ t('COMMISSIONS.IMPORT.UPLOAD_HINT') }}</p>
              <input
                ref="importFileInput"
                type="file"
                accept=".csv,text/csv"
                class="hidden"
                @change="onFileSelected"
              />
              <button
                class="bg-n-brand/10 text-n-brand border border-n-brand/30 text-sm font-medium px-4 py-2 rounded-lg hover:bg-n-brand/20 transition-colors"
                @click="importFileInput?.click()"
              >
                Escolher arquivo
              </button>
            </div>

            <div v-if="importFile" class="flex items-center gap-3 bg-n-alpha-1 border border-n-weak/30 rounded-lg px-4 py-3">
              <span class="i-lucide-file-text text-n-brand text-xl" />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">{{ importFile.name }}</p>
                <p class="text-xs text-n-slate-11">{{ (importFile.size / 1024).toFixed(1) }} KB</p>
              </div>
              <button
                :disabled="importLoading"
                class="bg-n-brand text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-n-brand/90 disabled:opacity-50 transition-colors"
                @click="previewImport"
              >
                <span v-if="importLoading" class="i-lucide-loader-circle animate-spin mr-1" />
                Pré-visualizar
              </button>
            </div>

            <p v-if="importError" class="text-sm text-red-400">{{ importError }}</p>
          </div>

          <!-- Step: Mapping -->
          <div v-else-if="importStep === 'mapping'" class="space-y-4">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-sm font-semibold text-n-slate-12">{{ t('COMMISSIONS.IMPORT.PREVIEW_TITLE') }}</h3>
                <p class="text-xs text-n-slate-11">{{ t('COMMISSIONS.IMPORT.PREVIEW_SUBTITLE') }}</p>
              </div>
              <button class="text-xs text-n-slate-11 hover:text-n-slate-12" @click="resetImport">
                <span class="i-lucide-arrow-left mr-1" />Voltar
              </button>
            </div>

            <!-- Mapeamento de colunas -->
            <div class="border border-n-weak/30 rounded-xl overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-n-alpha-2 border-b border-n-weak/30">
                  <tr>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11 w-1/2">{{ t('COMMISSIONS.IMPORT.CSV_COLUMN') }}</th>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11 w-1/2">{{ t('COMMISSIONS.IMPORT.SYSTEM_FIELD') }}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak/20">
                  <tr v-for="header in importHeaders" :key="header" class="hover:bg-n-alpha-1">
                    <td class="px-4 py-2.5 font-mono text-xs text-n-slate-12">{{ header }}</td>
                    <td class="px-4 py-2.5">
                      <select
                        v-model="importMapping[header]"
                        class="w-full text-xs border border-n-weak rounded-lg px-2 py-1.5 bg-n-alpha-1 text-n-slate-12"
                      >
                        <option value="">{{ t('COMMISSIONS.IMPORT.FIELD_IGNORE') }}</option>
                        <option v-for="field in IMPORT_DB_FIELDS" :key="field" :value="field">
                          {{ t(`COMMISSIONS.IMPORT.FIELDS.${field.toUpperCase()}`, field) }}
                        </option>
                      </select>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pré-visualização das primeiras linhas -->
            <div v-if="importPreviewRows.length > 0" class="border border-n-weak/30 rounded-xl overflow-x-auto">
              <p class="text-xs font-semibold text-n-slate-11 px-4 py-2 border-b border-n-weak/20">
                Pré-visualização ({{ importPreviewRows.length }} primeiras linhas)
              </p>
              <table class="w-full text-xs">
                <thead class="bg-n-alpha-2">
                  <tr>
                    <th v-for="h in importHeaders" :key="h" class="text-left px-3 py-2 text-n-slate-11 font-medium whitespace-nowrap">{{ h }}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak/10">
                  <tr v-for="(row, i) in importPreviewRows" :key="i" class="hover:bg-n-alpha-1">
                    <td v-for="h in importHeaders" :key="h" class="px-3 py-1.5 text-n-slate-11 whitespace-nowrap">{{ row[h] || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p v-if="importError" class="text-sm text-red-400">{{ importError }}</p>

            <div class="flex gap-3">
              <button
                :disabled="importLoading"
                class="bg-n-brand text-white text-sm font-medium px-6 py-2.5 rounded-lg hover:bg-n-brand/90 disabled:opacity-50 transition-colors"
                @click="confirmImport"
              >
                <span v-if="importLoading" class="i-lucide-loader-circle animate-spin mr-1" />
                {{ importLoading ? t('COMMISSIONS.IMPORT.IMPORTING') : t('COMMISSIONS.IMPORT.CONFIRM_IMPORT') }}
              </button>
              <button class="text-sm text-n-slate-11 px-4 hover:text-n-slate-12" @click="resetImport">
                {{ t('COMMISSIONS.IMPORT.CANCEL') }}
              </button>
            </div>
          </div>

          <!-- Step: Done -->
          <div v-else-if="importStep === 'done'" class="text-center py-8">
            <span class="i-lucide-check-circle-2 text-5xl text-emerald-500 block mb-3" />
            <p class="text-base font-semibold text-n-slate-12 mb-1">Importação iniciada!</p>
            <p class="text-sm text-n-slate-11 mb-4">{{ t('COMMISSIONS.IMPORT.SUCCESS') }}</p>
            <button
              class="bg-n-brand/10 text-n-brand border border-n-brand/30 text-sm font-medium px-4 py-2 rounded-lg hover:bg-n-brand/20 transition-colors"
              @click="resetImport"
            >
              Importar outro arquivo
            </button>
          </div>

          <!-- Histórico de importações -->
          <div class="mt-8">
            <h3 class="text-sm font-semibold text-n-slate-12 mb-3">{{ t('COMMISSIONS.IMPORT.HISTORY_TITLE') }}</h3>
            <div v-if="loadingImportHistory" class="text-center py-6 text-n-slate-11 text-sm">{{ t('COMMISSIONS.COMMON.LOADING') }}</div>
            <div v-else-if="importHistory.length === 0" class="text-center py-6 text-n-slate-11 text-sm">{{ t('COMMISSIONS.IMPORT.NO_HISTORY') }}</div>
            <div v-else class="border border-n-weak/30 rounded-xl overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-n-alpha-2 border-b border-n-weak/30">
                  <tr>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.IMPORT.TABLE.FILENAME') }}</th>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.IMPORT.TABLE.STATUS') }}</th>
                    <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.IMPORT.TABLE.TOTAL') }}</th>
                    <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.IMPORT.TABLE.PROCESSED') }}</th>
                    <th class="text-right px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.IMPORT.TABLE.ERRORS') }}</th>
                    <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.IMPORT.TABLE.DATE') }}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak/20">
                  <tr v-for="imp in importHistory" :key="imp.id" class="hover:bg-n-alpha-1">
                    <td class="px-4 py-3 text-n-slate-12 text-xs font-mono">{{ imp.filename || '—' }}</td>
                    <td class="px-4 py-3">
                      <span :class="[
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                        imp.status === 'completed' ? 'bg-emerald-500/10 text-emerald-500' :
                        imp.status === 'failed' ? 'bg-red-500/10 text-red-400' :
                        imp.status === 'processing' ? 'bg-blue-500/10 text-blue-500' :
                        'bg-n-alpha-3 text-n-slate-11'
                      ]">
                        {{ t(`COMMISSIONS.IMPORT.STATUS.${imp.status}`) }}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-right text-n-slate-11">{{ imp.total_rows ?? '—' }}</td>
                    <td class="px-4 py-3 text-right text-n-slate-11">{{ imp.processed_rows ?? '—' }}</td>
                    <td class="px-4 py-3 text-right" :class="imp.errors_count > 0 ? 'text-red-400 font-medium' : 'text-n-slate-11'">
                      {{ imp.errors_count ?? 0 }}
                    </td>
                    <td class="px-4 py-3 text-n-slate-11 text-xs">{{ new Date(imp.created_at * 1000).toLocaleString('pt-BR') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- ═══════════════════ AUDIT ═══════════════════ -->
        <div v-else-if="activeTab === 'audit'" class="space-y-4">
          <div v-if="loadingAudit" class="text-center py-12 text-n-slate-11">{{ t('COMMISSIONS.COMMON.LOADING') }}</div>
          <div v-else-if="auditLogs.length === 0" class="text-center py-12 text-n-slate-11">{{ t('COMMISSIONS.AUDIT.NO_DATA') }}</div>
          <div v-else class="border border-n-weak/30 rounded-xl overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-n-alpha-2 border-b border-n-weak/30">
                <tr>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.AUDIT.TABLE.ENTITY') }}</th>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.AUDIT.TABLE.ACTION') }}</th>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.AUDIT.TABLE.USER') }}</th>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-n-slate-11">{{ t('COMMISSIONS.AUDIT.TABLE.DATE') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak/20">
                <tr v-for="log in auditLogs" :key="log.id" class="hover:bg-n-alpha-1">
                  <td class="px-4 py-3 font-mono text-xs text-n-slate-11">{{ log.entity_type }} #{{ log.entity_id }}</td>
                  <td class="px-4 py-3">
                    <span :class="['inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', log.action === 'create' ? 'bg-emerald-500/10 text-emerald-500' : log.action === 'destroy' ? 'bg-red-500/10 text-red-400' : 'bg-blue-500/10 text-blue-500']">
                      {{ log.action }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-n-slate-11 text-xs">{{ log.user_name || log.user_id || '—' }}</td>
                  <td class="px-4 py-3 text-n-slate-11 text-xs">{{ new Date(log.created_at).toLocaleString('pt-BR') }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="auditTotalPages > 1" class="flex justify-center gap-2">
            <button :disabled="auditPage <= 1" class="px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-11 disabled:opacity-40" @click="auditPage--; fetchAuditLogs()">←</button>
            <span class="px-3 py-1.5 text-sm text-n-slate-11">{{ auditPage }} / {{ auditTotalPages }}</span>
            <button :disabled="auditPage >= auditTotalPages" class="px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-11 disabled:opacity-40" @click="auditPage++; fetchAuditLogs()">→</button>
          </div>
        </div>
      </div>
    </template>

    <!-- ═══════════════════ POLICY FORM MODAL ═══════════════════ -->
    <Teleport to="body">
      <div v-if="showPolicyForm" class="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div class="bg-n-background border border-n-weak/30 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak/30">
            <h3 class="font-semibold text-n-slate-12">{{ editingPolicy ? 'Editar Política' : 'Nova Política' }}</h3>
            <button class="text-n-slate-11 hover:text-n-slate-12" @click="showPolicyForm = false"><span class="i-lucide-x text-lg" /></button>
          </div>
          <div class="p-6 space-y-5">
            <!-- Campos básicos -->
            <div class="grid grid-cols-2 gap-4">
              <div class="col-span-2">
                <label class="text-xs text-n-slate-11 mb-1 block">Nome da Política</label>
                <input v-model="policyForm.name" type="text" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div class="col-span-2">
                <label class="text-xs text-n-slate-11 mb-1 block">Descrição</label>
                <textarea v-model="policyForm.description" rows="2" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 resize-none" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">Vigência de</label>
                <input v-model="policyForm.effective_from" type="date" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">Vigência até</label>
                <input v-model="policyForm.effective_until" type="date" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
            </div>

            <!-- Percentuais e teto -->
            <div>
              <p class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-3">Percentuais de Comissão</p>
              <div class="grid grid-cols-4 gap-4">
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">Uniproduto (%)</label>
                  <input v-model="policyForm.pct_uniproduto" type="number" step="0.01" min="0" max="100" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
                </div>
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">Completa (%)</label>
                  <input v-model="policyForm.pct_completa" type="number" step="0.01" min="0" max="100" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
                </div>
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">Aporte (%)</label>
                  <input v-model="policyForm.pct_aporte" type="number" step="0.01" min="0" max="100" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
                </div>
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">Teto Aporte (R$)</label>
                  <input v-model="policyForm.aporte_cap" type="number" step="1" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
                </div>
              </div>
            </div>

            <!-- Faixas de Volume -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <p class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">Faixas de Volume</p>
                <button class="text-xs text-n-brand hover:underline" @click="addVolumeTier">+ Faixa</button>
              </div>
              <div class="border border-n-weak/30 rounded-lg overflow-hidden">
                <table class="w-full text-xs">
                  <thead class="bg-n-alpha-2">
                    <tr>
                      <th class="text-left px-3 py-2 text-n-slate-11 font-medium">Mín. vendas</th>
                      <th class="text-left px-3 py-2 text-n-slate-11 font-medium">Máx. vendas</th>
                      <th class="text-left px-3 py-2 text-n-slate-11 font-medium">Bônus Uni (R$)</th>
                      <th class="text-left px-3 py-2 text-n-slate-11 font-medium">Bônus Compl. (R$)</th>
                      <th class="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-n-weak/20">
                    <tr v-for="(tier, idx) in policyForm.volume_tiers" :key="idx">
                      <td class="px-2 py-1.5"><input v-model.number="tier.min" type="number" min="0" class="w-full text-sm border border-n-weak rounded px-2 py-1 bg-n-alpha-1 text-n-slate-12" /></td>
                      <td class="px-2 py-1.5"><input v-model.number="tier.max" type="number" min="0" placeholder="∞" class="w-full text-sm border border-n-weak rounded px-2 py-1 bg-n-alpha-1 text-n-slate-12" /></td>
                      <td class="px-2 py-1.5"><input v-model.number="tier.uniproduto" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded px-2 py-1 bg-n-alpha-1 text-n-slate-12" /></td>
                      <td class="px-2 py-1.5"><input v-model.number="tier.completa" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded px-2 py-1 bg-n-alpha-1 text-n-slate-12" /></td>
                      <td class="px-2 py-1.5 text-center"><button class="text-red-400 hover:text-red-600" @click="removeVolumeTier(idx)"><span class="i-lucide-x text-sm" /></button></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Faixas Plus -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <p class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">Faixas de Premiação Plus</p>
                <button class="text-xs text-n-brand hover:underline" @click="addPlusTier">+ Faixa</button>
              </div>
              <div class="border border-n-weak/30 rounded-lg overflow-hidden">
                <table class="w-full text-xs">
                  <thead class="bg-n-alpha-2">
                    <tr>
                      <th class="text-left px-3 py-2 text-n-slate-11 font-medium">Ating. Mín (%)</th>
                      <th class="text-left px-3 py-2 text-n-slate-11 font-medium">Ating. Máx (%)</th>
                      <th class="text-left px-3 py-2 text-n-slate-11 font-medium">Bônus Fixo (R$)</th>
                      <th class="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-n-weak/20">
                    <tr v-for="(tier, idx) in policyForm.plus_tiers" :key="idx">
                      <td class="px-2 py-1.5"><input v-model.number="tier.min_pct" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded px-2 py-1 bg-n-alpha-1 text-n-slate-12" placeholder="ex: 1.00" /></td>
                      <td class="px-2 py-1.5"><input v-model.number="tier.max_pct" type="number" step="0.01" placeholder="∞" class="w-full text-sm border border-n-weak rounded px-2 py-1 bg-n-alpha-1 text-n-slate-12" /></td>
                      <td class="px-2 py-1.5"><input v-model.number="tier.bonus" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded px-2 py-1 bg-n-alpha-1 text-n-slate-12" /></td>
                      <td class="px-2 py-1.5 text-center"><button class="text-red-400 hover:text-red-600" @click="removePlusTier(idx)"><span class="i-lucide-x text-sm" /></button></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Ativa -->
            <div class="flex items-center gap-3">
              <input id="policy-active" v-model="policyForm.is_active" type="checkbox" class="rounded" />
              <label for="policy-active" class="text-sm text-n-slate-12">Política ativa</label>
            </div>

            <div class="flex gap-3 pt-2">
              <button :disabled="savingPolicy" class="flex-1 bg-n-brand text-white text-sm font-medium py-2.5 rounded-lg hover:bg-n-brand/90 disabled:opacity-50 transition-colors" @click="savePolicy">
                {{ savingPolicy ? 'Salvando...' : 'Salvar Política' }}
              </button>
              <button class="px-4 text-sm text-n-slate-11 hover:text-n-slate-12" @click="showPolicyForm = false">Cancelar</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ═══════════════════ GOAL CONFIG FORM MODAL ═══════════════════ -->
    <Teleport to="body">
      <div v-if="showGoalForm" class="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div class="bg-n-background border border-n-weak/30 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak/30">
            <h3 class="font-semibold text-n-slate-12">{{ editingGoalConfig ? 'Editar Meta' : 'Nova Meta de Equipe' }}</h3>
            <button class="text-n-slate-11 hover:text-n-slate-12" @click="showGoalForm = false"><span class="i-lucide-x text-lg" /></button>
          </div>
          <div class="p-6 space-y-4">
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Competência (YYYY-MM)</label>
              <input v-model="goalForm.competence" type="month" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
            </div>
            <div class="grid grid-cols-3 gap-4">
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">Meta Previdência (R$)</label>
                <input v-model="goalForm.goal_previdencia" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">Meta Risco (R$)</label>
                <input v-model="goalForm.goal_risco" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">Meta Total (R$)</label>
                <input v-model="goalForm.goal_total" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Política vinculada</label>
              <select v-model="goalForm.commission_policy_id" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12">
                <option value="">— Nenhuma —</option>
                <option v-for="p in policies" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Consultor específico (opcional)</label>
              <select v-model="goalForm.user_id" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12">
                <option value="">— Global (todos) —</option>
                <option v-for="agent in agents" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
              </select>
            </div>
            <div class="flex gap-3 pt-2">
              <button :disabled="savingGoal" class="flex-1 bg-n-brand text-white text-sm font-medium py-2.5 rounded-lg hover:bg-n-brand/90 disabled:opacity-50 transition-colors" @click="saveGoalConfig">
                {{ savingGoal ? 'Salvando...' : 'Salvar Meta' }}
              </button>
              <button class="px-4 text-sm text-n-slate-11 hover:text-n-slate-12" @click="showGoalForm = false">Cancelar</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ═══════════════════ CAMPAIGN FORM MODAL ═══════════════════ -->
    <Teleport to="body">
      <div v-if="showCampaignForm" class="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div class="bg-n-background border border-n-weak/30 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak/30">
            <h3 class="font-semibold text-n-slate-12">{{ editingCampaign ? 'Editar Campanha' : 'Nova Campanha Extra' }}</h3>
            <button class="text-n-slate-11 hover:text-n-slate-12" @click="showCampaignForm = false"><span class="i-lucide-x text-lg" /></button>
          </div>
          <div class="p-6 space-y-4">
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Nome da Campanha</label>
              <input v-model="campaignForm.name" type="text" placeholder="Ex: Campanha Abril de Ferro" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Descrição</label>
              <textarea v-model="campaignForm.description" rows="2" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 resize-none" />
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Competência</label>
              <input v-model="campaignForm.competence" type="month" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Tipo de Critério</label>
              <select v-model="campaignForm.criteria_type" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12">
                <option v-for="ct in criteriaTypes" :key="ct.value" :value="ct.value">{{ ct.label }}</option>
              </select>
            </div>
            <div v-if="campaignForm.criteria_type !== 'manual'">
              <label class="text-xs text-n-slate-11 mb-1 block">
                Valor mínimo
                <span class="text-n-slate-11">({{ campaignForm.criteria_type === 'min_new_sales' ? 'quantidade de vendas' : 'em R$' }})</span>
              </label>
              <input v-model="campaignForm.criteria_value" type="number" step="1" min="0" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Bônus por consultor elegível (R$)</label>
              <input v-model="campaignForm.bonus_amount" type="number" step="0.01" min="0" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
            </div>
            <div class="flex gap-3 pt-2">
              <button :disabled="savingCampaign" class="flex-1 bg-n-brand text-white text-sm font-medium py-2.5 rounded-lg hover:bg-n-brand/90 disabled:opacity-50 transition-colors" @click="saveCampaign">
                {{ savingCampaign ? 'Salvando...' : 'Salvar Campanha' }}
              </button>
              <button class="px-4 text-sm text-n-slate-11 hover:text-n-slate-12" @click="showCampaignForm = false">Cancelar</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ═══════════════════ LAUNCH FORM MODAL ═══════════════════ -->
    <Teleport to="body">
      <div v-if="showLaunchForm" class="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div class="bg-n-background border border-n-weak/30 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak/30">
            <h3 class="font-semibold text-n-slate-12">
              {{ editingLaunch ? t('COMMISSIONS.LAUNCHES.FORM.TITLE_EDIT') : t('COMMISSIONS.LAUNCHES.FORM.TITLE_CREATE') }}
            </h3>
            <button class="text-n-slate-11 hover:text-n-slate-12" @click="showLaunchForm = false">
              <span class="i-lucide-x text-lg" />
            </button>
          </div>
          <div class="p-6 space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div class="col-span-2">
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.CONSULTANT') }}</label>
                <select v-model="launchForm.user_id" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12">
                  <option value="">Selecionar consultor...</option>
                  <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                    {{ agent.name }} ({{ agent.email }})
                  </option>
                </select>
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.COMPETENCE') }}</label>
                <input v-model="launchForm.competence" type="month" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.SALE_DATE') }}</label>
                <input v-model="launchForm.sale_date" type="date" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.PARTICIPANTE') }}</label>
                <input v-model="launchForm.participante" type="text" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.CPF') }}</label>
                <input v-model="launchForm.cpf" type="text" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.PROPOSTA') }}</label>
                <input v-model="launchForm.proposta" type="text" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.INSCRICAO') }}</label>
                <input v-model="launchForm.inscricao" type="text" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div class="col-span-2">
                <label class="text-xs text-n-slate-11 mb-1 block">Produto</label>
                <select v-model="launchForm.product_id" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12">
                  <option value="">Selecionar...</option>
                  <option v-for="p in products" :key="p.id" :value="p.id">{{ p.code }} — {{ p.name }}</option>
                </select>
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.VAL_PREVIDENCIA') }}</label>
                <input v-model="launchForm.val_previdencia" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.VAL_RISCO_INVALIDEZ') }}</label>
                <input v-model="launchForm.val_risco_invalidez" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.VAL_RISCO_MORTE') }}</label>
                <input v-model="launchForm.val_risco_morte" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.VAL_APORTE') }}</label>
                <input v-model="launchForm.val_aporte" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.VAL_PORTABILIDADE') }}</label>
                <input v-model="launchForm.val_portabilidade" type="number" step="0.01" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12" />
              </div>
              <div class="col-span-2">
                <label class="text-xs text-n-slate-11 mb-1 block">{{ t('COMMISSIONS.LAUNCHES.FORM.NOTES') }}</label>
                <textarea v-model="launchForm.notes" rows="2" class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 resize-none" />
              </div>
            </div>
            <div class="flex gap-3 pt-2">
              <button
                :disabled="savingLaunch"
                class="flex-1 bg-n-brand text-white text-sm font-medium py-2.5 rounded-lg hover:bg-n-brand/90 disabled:opacity-50 transition-colors"
                @click="saveLaunch"
              >{{ t('COMMISSIONS.LAUNCHES.FORM.SAVE') }}</button>
              <button class="px-4 text-sm text-n-slate-11 hover:text-n-slate-12" @click="showLaunchForm = false">
                {{ t('COMMISSIONS.LAUNCHES.FORM.CANCEL') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
